import React, { useContext, useEffect, useRef, useState } from 'react';
import IContract from 'src/models/contract_types/IContract';
import { AppContext } from 'src/providers/AppContextProvider';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import ContractGenerationOwnerData from '../ContractGenerationModels/ContractGenerationOwnerData';
import OwnerUtil from 'src/util/OwnerUtil';
import OwnerIndividual from 'src/models/tract_detail/add_ownership_revised/OwnerIndividual';
import Button from 'src/components/controls/forms/redesign/FormButton';
import OwnershipContactCategoryForm from 'src/models/OwnershipContactCategoryForm';
import useFetch from 'use-http';
import { Controller, useForm } from 'react-hook-form';
import ContractAmendment from '../ContractGenerationModels/ContractAmendment';
import moment from 'moment';
import ContractAddendumClauseXRef from '../ContractGenerationModels/ContractAddendumClauseXRef';
import ContractGenerationTractDetail from '../ContractGenerationModels/ContractGenerationTractDetail';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import LeaseClauseType from 'src/models/LeaseClauseType';
import ContractAddendumClause from '../ContractGenerationModels/ContractAddendumClause';
import actions from 'src/actions';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import PagedResultSet from 'src/models/common/PagedResultSet';
import State from 'src/models/State';
import OwnerJointTenant from 'src/models/tract_detail/add_ownership_revised/OwnerJointTenant';
import LeasingTractList from '../../LeasingTractList.redesign';
import AgreementUtil from 'src/util/AgreementUtil';
import { IRootState } from 'src/reducers';
import AgreementDocs from '../../AgreementDocs';
import ClientApprovedLeaseClauses from '../../ClientApprovedLeaseClauses';
import DialogList from 'src/dialogs';
import AdHocClauses from '../AdHocClauses';
import ContractAdHocClause from '../ContractGenerationModels/ContractAdHocClause';
import AcknowledgementType from 'src/models/leasing/AcknowledgementType';
// import RHFToggle from 'src/components/controls/tempRHFControls/RHFToggle';
import LocationModel from 'src/models/leasing/LocationModel';
import AcknowledgementList from '../AcknowledgementList';
import ContractPdfGeneration from '../ContractGenerationModels/ContractPdfGeneration';
import IndexReferenceType from 'src/models/IndexReferenceType';
import Lease from 'src/models/leasehold/Lease';
import SurfaceWaiver, { SurfaceWaiverHold } from 'src/models/contract_types/SurfaceWaiver';
import BatteryStorage, { BatteryStorageHold } from 'src/models/contract_types/BatteryStorage';
import LandContractType from 'src/models/LandContractType';
import BatteryStorageGeneration from '../BatteryStorageComponents/BatteryStorageGeneration';
import SurfaceWaiverGeneration from '../SurfaceWaiverComponents/SurfaceWaiverGeneration';
import SolarLeaseGeneration from '../SolarLeaseComponents/SolarLeaseGeneration';
import SolarLease from 'src/models/contract_types/SolarLease';
import BrineLease, { BrineLeaseHold } from 'src/models/contract_types/BrineLease';
import ProvisionClauseLeaseholdXRef from 'src/models/ProvisionClauseLeaseholdXRef';
import BrineLeaseGeneration from '../BrineLeaseComponents/BrineLeaseGeneration';
import IOwner from 'src/models/IOwner';
import { LandSystem } from 'src/models/LandSystem';
import ReportFileDetails from 'src/models/ReportFileDetails';
import * as Constants from 'src/components/common/Constants';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EntityModificationContract from 'src/models/entityModificationLogs/EntityModificationContract';
import ProvisionClause from 'src/components/common/ProvisionClause';
import CalmsInvoiceData from 'src/models/CalmsInvoiceData';
import MathUtil from 'src/util/MathUtil';

const ContractGenerationPage = () => {
    // CONTEXT
    const appContext = useContext<IAppContextState>(AppContext);
    const dialogContext = useContext<dialogCreators>(DialogContext);

    // REACT LIBRARIES
    const { get, put, post, del, response } = useFetch();
    const dispatch = useDispatch();
    const { register, handleSubmit, errors, setValue, getValues, reset, formState, control } = useForm();
    const { isDirty } = formState;

    // REDUCERS
    const { project } = useSelector((state: IRootState) => state);

    // STATE LIBRARIES
    // ~Form Data
    const [contractData, setContractData] = useState<IContract>(undefined);
    const [contractClauses, setContractClauses] = useState<ContractAddendumClause[]>(undefined);
    const [ownerData, setOwnerData] = useState<ContractGenerationOwnerData>(undefined);
    const [ownershipId, setOwnershipId] = useState<number>(undefined);
    const [contractId, setContractId] = useState<number>(undefined);
    const [leaseTractList, setLeaseTractList] = useState<ContractGenerationTractDetail[]>([]);
    const [originalTractList, setOriginalTractList] = useState<ContractGenerationTractDetail[]>([]);
    const [defaultLessee, setDefaultLessee] = useState(null);
    const [totalGross, setTotalGross] = useState<number>(0);
    const [totalNet, setTotalNet] = useState<number>(0);
    const [selectedClauses, setSelectedClauses] = useState<Array<number>>([]);
    const [adHocClauses, setAdHocClauses] = useState<ContractAdHocClause[]>([]);
    const [location, setLocation] = useState<LocationModel>(undefined);
    const [tractState, setTractState] = useState<number>(undefined);
    const [amendments, setAmendments] = useState<ContractAmendment[]>([]);
    const [attachedProvisionClauses, setAttachedProvisionClauses] = useState<Array<ProvisionClauseLeaseholdXRef>>(undefined);
    // ~New Reference Data
    const [invoiceableLookupId, setInvoiceableLookupId] = useState<number>(undefined);
    const [leaseNumber, setLeaseNumber] = useState<number>(undefined);
    const [contractLookupId, setContractLookupId] = useState<number>(undefined);

    // ~Dropdown Data
    const [projectContactCategories, setProjectContactCategories] = useState<OwnershipContactCategoryForm>(undefined);
    const [leaseClauseTypes, setLeaseClauseTypes] = useState<LeaseClauseType[]>([]);
    const [states, setStates] = useState<PagedResultSet<State>>(undefined);
    const [acknowledgementTypes, setAcknowledgementTypes] = useState<AcknowledgementType[]>([]);
    const [counties, setCounties] = useState<string[]>(undefined);
    const [measurementTypes, setMeasurementTypes] = useState<Array<IndexReferenceType>>([]);
    const [provisionLeaseClauses, setProvisionLeaseClauses] = useState<Array<IndexReferenceType>>(undefined);

    // ~Page Context
    const [tractContextType, setTractContextType] = useState<Array<string>>([]);
    const [contractType, setContractType] = useState<LandContractType>(undefined);
    const [contractTypeName, setContractTypeName] = useState<string>('');
    const [controllerPath, setControllerPath] = useState<string>('');
    const [tractsSelected, setTractsSelected] = useState<boolean>(false);
    const [isNewContract, setIsNewContract] = useState<boolean>(false);
    const [deltasAdded, setDeltasAdded] = useState<boolean>(false);
    const [secondWindowOpen, setSecondWindowOpen] = useState<boolean>(false);
    const [isInHand, setIsInHand] = useState<boolean>(false);
    const [autoPopulateLessee, setAutoPopulateLessee] = useState<object>({ value: 0 });
    const [moveLegalDescription, setMoveLegalDescription] = useState<string>(undefined);
    const [pdfRenderTypeId, setPdfRenderTypeId] = useState<number>(0);
    const [actionType, setActionType] = useState<DialogList>(undefined);
    const [formLoading, setFormLoading] = useState<boolean>(true);
    const [tractMORList, setTractMORList] = useState<ReportFileDetails[]>(undefined);
    const [rerender, setRerender] = useState<object>({ value: 0 });
    const [invoiceData, setInvoiceData] = useState<CalmsInvoiceData[]>(undefined);
    const [hasOlderTemplates, setHasOlderTemplates] = useState<boolean>(false);

    // Navigation properties
    const agreementTermSection = useRef(null);
    const generInfoSection = useRef(null);
    const lesseeInfoSection = useRef(null);
    const ownerInfoSection = useRef(null);
    const docManagSection = useRef(null);
    const tractListSection = useRef(null);
    const provisionSection = useRef(null);
    const addendumSection = useRef(null);
    const adHocSection = useRef(null);
    const pmSection = useRef(null);
    const pmAdmendmentSec = useRef(null);
    const acknowledgementSec = useRef(null);

    // USE EFFECTS
    useEffect(() => {
        getStates();
        getProjectContactCategories();
        getAcknowledgementTypes();
        getContractClauseTypes();
        getContractClauses();
        getMeasurementTypes();

        // TODO: Look into setting this up without the need for so many state variables.
        if (window.location.pathname.indexOf('/leases') > -1) {
            setTractContextType(['Mineral']);
            setContractType(LandContractType.OilAndGas);
            setContractTypeName('Oil and Gas Lease');
            setContractId(appContext.leaseId ? appContext.leaseId : 0);
            setControllerPath('leases');
        } else if (window.location.pathname.indexOf('/solar-leases') > -1) {
            setTractContextType(['Surface']);
            setContractType(LandContractType.SolarLease);
            setContractTypeName('Solar Lease');
            setContractId(appContext.solarLeaseId ? appContext.solarLeaseId : 0);
            setControllerPath('solarLease');
        } else if (window.location.pathname.indexOf('/surface-waivers') > -1) {
            setTractContextType(['Mineral']);
            setContractType(LandContractType.SurfaceWaiver);
            setContractTypeName('Surface Waiver');
            setContractId(appContext.surfaceWaiverId ? appContext.surfaceWaiverId : 0);
            setControllerPath('surfaceWaiver');
        } else if (window.location.pathname.indexOf('/battery-storage') > -1) {
            setTractContextType(['Surface']);
            setContractType(LandContractType.BatteryStorage);
            setContractTypeName('Battery Storage');
            setContractId(appContext.batteryStorageId ? appContext.batteryStorageId : 0);
            setControllerPath('batteryStorage');
        } else if (window.location.pathname.indexOf('/brine-leases') > -1) {
            setTractContextType(['Mineral']);
            setContractType(LandContractType.BrineLease);
            setContractTypeName('Brine Lease');
            setContractId(appContext.brineLeaseId ? appContext.brineLeaseId : 0);
            setControllerPath('brineLease');
        }

        if (appContext.ownerId > 0) {
            getOwnerData();
        }
    }, []);

    useEffect(() => {
        console.log('Rerender so everything shows up!');
    }, [rerender]);

    useEffect(() => {
        if (ownerData?.tracts?.length < 1) {
            useDefaultLessee();
        }
    }, [autoPopulateLessee]);

    useEffect(() => {
        setAutoPopulateLessee({ value: 1 });
    }, [defaultLessee, ownerData]);

    useEffect(() => {
        if (!!contractType) {
            getProvisionClauses();
        }
    }, [contractType]);

    useEffect(() => {
        if (!!contractData && !!ownerData && !!states && !!provisionLeaseClauses) {
            reset(initialValues());
            setFormLoading(false);
            setRerender({ value: 1 });
        }
    }, [contractData, provisionLeaseClauses, attachedProvisionClauses, ownerData, states]);

    useEffect(() => {
        if (!!contractData) {
            setMoveLegalDescription(contractData.legalDescription);
            if (contractData.tracts?.length > 0) {
                let ownership = !!ownershipId ? ownershipId : contractData?.tracts?.find((st) => st.includeTract === true)?.ownershipId;
                setOwnershipId(ownership);
            }
        }

        if (!!contractData?.contractAmendments) {
            setAmendments(contractData.contractAmendments);
        }

        if (contractData?.tracts?.length > 0) {
            let acreCount = AgreementUtil.calcTotalAcreages(contractData?.tracts, project?.currentProject?.landSystemId);
            setTotalNet(acreCount[1]);
        }
        setIsInHand(!!contractData?.isInHand);

        if (!!contractData && !!ownerData && !!project.currentProject) {
            addressRendering(ownerData?.tracts);

            if (contractId > 0 && ownerData?.tracts?.length > 0 && contractData?.tracts?.length > 0 && !contractData?.inHandDate) {
                let hasChanges = AgreementUtil.findLeaseTractChanges(ownerData.tracts, contractData.tracts, project?.currentProject?.landSystemId);
                if (hasChanges) {
                    showTractListDeltaDialog();
                }
            }
        }

    }, [contractData, ownerData, states, project.currentProject]);

    useEffect(() => {
        if (contractId > 0) {
            getContractData();
        }
    }, [contractId]);

    useEffect(() => {
        if (actionType && pdfRenderTypeId > 0) {
            dispatch(actions.layout.showDialog(actionType));
            setActionType(undefined);
        }
    }, [actionType, pdfRenderTypeId]);

    useEffect(() => {
        if (project?.currentProject?.defaultOwnerLookUpId > 0) {
            getDefaultLessee();
        }
    }, [project?.currentProject?.defaultOwnerLookUpId]);

    useEffect(() => {
        if (!!secondWindowOpen) {
            setTimeout(() => {
                infoToSecondWindow();
            }, 2000);
        }
    }, [secondWindowOpen]);

    useEffect(() => {
        if (!(contractId > 0) && !!ownerData) {
            reset(initialValuesNewLease());
            setFormLoading(false);
        }
    }, [ownerData, contractId]);

    useEffect(() => {
        if (!!tractState && !!counties) {
            tractLocationInfo();
        }
    }, [tractState, counties]);

    useEffect(() => {
        if (!!contractData) {
            switch (contractType) {
                case LandContractType.OilAndGas:
                    let lease = contractData as Lease;
                    setLeaseNumber(parseInt(lease.leaseNumber, 10));
                    setContractLookupId(lease?.leaseholds?.[0].contractLookupId);
                    break;
                case LandContractType.SurfaceWaiver:
                    let waiver = contractData as SurfaceWaiver;
                    setLeaseNumber(waiver.surfaceWaiverNumber);
                    setContractLookupId(waiver?.surfaceWaiverHolds?.[0].contractLookupId);
                    break;
                case LandContractType.SolarLease:
                    let solar = contractData as SolarLease;
                    setLeaseNumber(solar.solarLeaseNumber);
                    setContractLookupId(solar?.solarLeaseHolds?.[0].contractLookupId);
                    break;
                case LandContractType.BatteryStorage:
                    let bess = contractData as BatteryStorage;
                    setLeaseNumber(bess.dlcLeaseNumber);
                    setContractLookupId(bess?.batteryStorageHolds?.[0].contractLookupId);
                    break;
                case LandContractType.BrineLease:
                    let brine = contractData as BrineLease;
                    setLeaseNumber(brine.leaseNumber);
                    setContractLookupId(brine?.brineLeaseHolds?.[0].contractLookupId);
                    getAttachedProvisionClauses(brine?.brineLeaseHolds?.[0].contractLookupId);
                    getProvisionClauses();
                    break;
                default:
                    break;
            }
        }
    }, [contractType, contractData]);

    useEffect(() => {
        if (!!contractData && !!contractClauses) {
            let currentSelection = [];
            for (let clause of contractData.contractClauses) {
                currentSelection.push(contractClauses.indexOf(contractClauses.find(cc => cc.id === clause.contractAddendumClauseId)));
            }
            setSelectedClauses(currentSelection);
        }
    }, [contractData, contractClauses]);

    useEffect(() => {
        if (leaseNumber > 0) {
            getCalmsInfo(leaseNumber);
        }
    }, [leaseNumber]);

    // HTTP GETS
    const getProjectContactCategories = async () => {
        let result = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/OwnershipContactCategories`);

        if (response.ok) {
            setProjectContactCategories(result);
        } else {
            console.log('Unable to get Project Contact Categories!!');
        }
    };

    const getContractClauses = async () => {
        const result = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/clauses/contract-clauses`);

        if (response.ok) {
            setContractClauses(result);
        } else {
            window.alert('getLeaseClauses call failed');
        }
    };

    const getContractClauseTypes = async () => {
        const result = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/lease-clause-types`);

        if (response.ok) {
            setLeaseClauseTypes(result);
        } else {
            console.log('Unable to get Lease Clause Types');
        }
    };

    const getContractData = async () => {
        let result = null;
        setDeltasAdded(false);

        if (contractId > 0) {
            result = await get(
                `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${ownershipId}/${controllerPath}/${contractId}`
            );

            if (response.ok) {
                console.log('Contract Data, ', result);
                setContractData(result);
                setIsNewContract(false);
            } else {
                window.alert('get contract data call failed');
            }
        } else {
            window.alert(`Invalid contract id: ${contractId}`);
        }
    };

    const getCalmsInfo = async (calmsLeaseNumber: number) => {
        const data = await get(`v1.0/calms/lease/${calmsLeaseNumber}`);

        if (response.ok) {
            try {
                const calmsData = JSON.parse(data.result);
                setInvoiceData(calmsData.items);
            } catch (e) {
                console.log('Error getting Calms info:', e);
                setInvoiceData(undefined);
            }
        }
    };

    const getOwnerData = async () => {
        if (!isNaN(appContext.ownerId) && appContext.ownerId > 0) {
            const result = await get(
                `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leasegeneration/owner/${appContext.ownerId}`
            );

            if (response.ok) {
                setOwnerData(result);
                console.log('owner data, ', result);
            } else {
                window.alert('getOwnerData call failed');
            }
        }
    };

    const getDefaultLessee = async () => {
        const result = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/owners/${project?.currentProject?.defaultOwnerLookUpId}`
        );

        if (response.ok) {
            setDefaultLessee(OwnerUtil.MapOwner(result));
        // } else {
        //     window.alert('get Default Lessee call failed');
        }
    };

    const getStates = async () => {
        let result = await get('/v1.0/states');

        if (response.ok) {
            setStates(result);
        } else {
            console.log('Unable to get the States Data!!!');
        }
    };

    const getAcknowledgementTypes = async () => {
        let result = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contractGeneration/acknowledgement-types`);

        if (response.ok) {
            setAcknowledgementTypes(result);
        } else {
            console.log('Unable to get Acknowledgement Types');
        }
    };

    const getMeasurementTypes = async () => {
        const result = await get(`/v1.0/referenceTypes`);

        if (response.ok) {
            setMeasurementTypes(result);
        }
    };

    const getAttachedProvisionClauses = async (lookupId: number) => {
        const result = await get(`/v1.0/provisionLeaseClause/contracts/${lookupId}`);

        if (response.ok) {
            setAttachedProvisionClauses(result);
        }
    };

    const getProvisionClauses = async () => {
        const result: IndexReferenceType[] = await get(
            `/v1.0/provisionLeaseClause/contract-type/${contractType}`
        );

        if (response.ok) {
            setProvisionLeaseClauses(result);
        } else {
            window.alert('Could not get Provision Lease Clauses!');
        }
    };

    // HTTP POST/PUT
    const createContract = async (data: IContract) => {
        let ownership = !!ownershipId ? ownershipId : data?.tracts?.find((st) => st.includeTract === true)?.ownershipId;

        // This is a provisional owner because it containts no tracts and 1 ownership
        if (ownership === undefined && ownerData?.tracts?.length < 1 && ownerData?.ownerships.length === 1) {
            ownership = ownerData?.ownerships?.[0].id;
        }
        setOwnershipId(ownership);
        const result = await post(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${ownership}/${controllerPath}`,
            data
        );

        if (response.ok) {
            setIsNewContract(false);
            dispatch(push(`${result.contractId}`));
            handleSuccessfulSubmit(result.contractId);
        } else if (result?.messageCode === '15') {
            window.alert('CALMS Update failed');
            setFormLoading(false);
        } else {
            window.alert('Create Lease failed');
            setFormLoading(false);
        }
    };

    const updateContract = async (data: IContract) => {
        let ownership = !!ownershipId ? ownershipId : data?.tracts?.find((st) => st.includeTract === true)?.ownershipId;

        // This is a provisional owner because it containts no tracts and 1 ownership
        if (ownership === undefined && ownerData?.tracts?.length < 1 && ownerData?.ownerships.length === 1) {
            ownership = ownerData?.ownerships?.[0].id;
        }

        setOwnershipId(ownership);
        let result = await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${ownership}/${controllerPath}`,
            data
        );

        if (response.ok) {
            handleSuccessfulSubmit(contractId);
            // TODO: Update this to include other contract types.
            if (contractType === LandContractType.BrineLease) {
                logContractInformation(data);
            }
        } else if (result?.messageCode === '15') {
            window.alert('CALMS Update failed');
            setFormLoading(false);
        } else {
            window.alert('Update Lease failed');
            setFormLoading(false);
        }
    };

    const addLeaseholdClauses = async (data: Array<ProvisionClauseLeaseholdXRef>) => {
        await post(
            `/v1.0/provisionLeaseClause`,
            data
        );

        if (response.ok) {
            getAttachedProvisionClauses(contractLookupId);
        }
    };

    const updateLeaseholdClauses = async (data: Array<ProvisionClauseLeaseholdXRef>) => {
        await put(
            `/v1.0/provisionLeaseClause`,
            data
        );

        if (response.ok) {
            getAttachedProvisionClauses(contractLookupId);
        }
    };

    const deleteLeaseholdClauses = async (deleteClauseIds: Array<number>) => {
        let query = '';
        for (let id of deleteClauseIds) {
            if (query.length > 0) {
                query += `&deleteClauseIds=${id}`;
            } else {
                query += `?deleteClauseIds=${id}`;
            }
        }

        await del(
            `/v1.0/provisionLeaseClause/contracts/${contractLookupId}${query}`,
            deleteClauseIds
        );

        if (response.ok) {
            getAttachedProvisionClauses(contractLookupId);
        }
    };

    // HANDLE DATA UPDATES
    const initialValues = () => {
        let initializedData: IContract = { ...contractData };

        setInvoiceableLookupId(initializedData.invoiceableLookupId);

        if (initializedData) {
            initializedData.effectiveDate = initializedData.effectiveDate
                ? (moment(initializedData.effectiveDate).format('YYYY-MM-DD') as unknown as Date)
                : null;
            // initializedData.contractDate = initializedData.contractDate
            //     ? (moment(initializedData.contractDate).format('YYYY-MM-DD') as unknown as Date)
            //     : null;
            initializedData.createdDate = initializedData.createdDate
                ? (moment(initializedData.createdDate).format('YYYY-MM-DD') as unknown as Date)
                : null;
            initializedData.commitDate = initializedData.commitDate
                ? (moment(initializedData.commitDate).format('YYYY-MM-DD') as unknown as Date)
                : null;
            initializedData.inHandDate = initializedData.inHandDate
                ? (moment(initializedData.inHandDate).format('YYYY-MM-DD') as unknown as Date)
                : null;
            initializedData.voidDate = initializedData.voidDate ? (moment(initializedData.voidDate).format('YYYY-MM-DD') as unknown as Date) : null;
            initializedData.recordedDate = initializedData.recordedDate
                ? (moment(initializedData.recordedDate).format('YYYY-MM-DD') as unknown as Date)
                : null;

            initializedData.rteLegalDescription = initializedData.rteLegalDescription?.length > 0 ? initializedData.rteLegalDescription : AgreementUtil.formatTractListLegal(initializedData.tracts);
        }

        if (initializedData?.tracts?.length > 0 && ownerData?.tracts?.length > 0) {
            let correctTracts: Array<ContractGenerationTractDetail> = [];

            ownerData.tracts.forEach((t: ContractGenerationTractDetail) => {
                let match: ContractGenerationTractDetail = initializedData?.tracts.find(
                    (lt: ContractGenerationTractDetail) => lt.ownershipId === t.ownershipId
                );

                if (!!match) {
                    match.sectionNumber = t.sectionNumber;
                    match.tractNumber = t.tractNumber;
                    match.surfaceTractNumber = t.surfaceTractNumber;
                    match.horizonNumber = t.horizonNumber;
                    correctTracts.push(match);
                }

                if (!match) {
                    correctTracts.push(t);
                }
            });

            initializedData.tracts = correctTracts.length > 0 ? correctTracts : initializedData.tracts;

            setLeaseTractList(initializedData.tracts);
        }

        if (initializedData?.tracts?.length > 0) {
            setTractsSelected(true);
        }

        if (initializedData?.adHocClauses?.length > 0) {
            setAdHocClauses(initializedData.adHocClauses);
        }

        if (contractClauses?.length > 0) {
            initializedData.contractClauses = [];
            for (let i = 0; i < contractClauses.length; i++) {
                let clauseSelected = !!contractData.contractClauses.find(cc => cc.contractAddendumClauseId === contractClauses[i].id);
                let addendumClauseId = clauseSelected ? contractClauses[i].id : 0;
                initializedData.contractClauses.push({ contractAddendumClauseId: addendumClauseId });
            }
        }

        const tempTractList: Array<ContractGenerationTractDetail> = contractData?.tracts;
        let totalAcreages = [0, 0];

        if (tempTractList?.length > 0) {
            totalAcreages = AgreementUtil.calcTotalAcreages(tempTractList, project?.currentProject?.landSystemId);

            setTotalGross(totalAcreages[0]);
            setTotalNet(totalAcreages[1]);
        }

        switch (contractType) {
            case LandContractType.BatteryStorage:
                initializedData = prepBatteryStorage(initializedData as BatteryStorage, totalAcreages[1]);
                break;
            case LandContractType.SurfaceWaiver:
                initializedData = prepSurfaceWaiver(initializedData as SurfaceWaiver, totalAcreages[1]);
                break;
            case LandContractType.SolarLease:
                initializedData = prepSolarLease(initializedData as SolarLease);
                break;
            case LandContractType.BrineLease:
                initializedData = prepBrineLease(initializedData as BrineLease, totalAcreages[1]);
                break;
            case LandContractType.OilAndGas:
            default:
                break;
        }

        return initializedData;
    };

    // Prep unique contract fields.
    const prepBatteryStorage = (data: BatteryStorage, netAcres: number) => {
        data.leaseDate = data.leaseDate = data.leaseDate
            ? (moment(data.leaseDate).format('YYYY-MM-DD') as unknown as Date)
            : null;

        if (data.batteryStorageHolds?.length > 0) {
            let hold = data.batteryStorageHolds[0];
            hold.executionBonusTotal = parseFloat(calculateTotalBonusPaid(hold.executionBonusTypeId, hold.executionBonus, netAcres));
            let subAcreage = hold.substationAcres > 0 ? hold.substationAcres : netAcres;
            hold.substationFeeTotal = parseFloat(calculateTotalBonusPaid(hold.substationFeeTypeId, hold.substationFee, subAcreage));
        }    

        return data;
    };

    const prepSurfaceWaiver = (data: SurfaceWaiver, netAcres: number) => {
        data.contractDate = data.contractDate
            ? (moment(data.contractDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.createdDate = data.createdDate
            ? (moment(data.createdDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.recordDate = data.recordDate
            ? (moment(data.recordDate).format('YYYY-MM-DD') as unknown as Date)
            : null;

        if (data.surfaceWaiverHolds?.length > 0) {
            let hold = data.surfaceWaiverHolds[0];
            hold.totalBonusPaid = parseFloat(calculateTotalBonusPaid(hold.bonusPaidTypeId, hold.bonusPaid, netAcres));
        }

        return data;
    };

    const prepSolarLease = (data: SolarLease) => {
        data.rentDueDate = data.rentDueDate
            ? (moment(data.rentDueDate).format('YYYY-MM-DD') as unknown as Date)
            : null;

        // const currentLeasehold = solarLeaseData?.solarLeaseHolds?.find((lh: SolarLeaseHold) => lh.surfaceOwner?.ownerLookUpId === appContext.ownerId);
        const currentLeasehold = data?.solarLeaseHolds[0];

        if (currentLeasehold) {
            currentLeasehold.dueDiligenceEffectiveDate = currentLeasehold.dueDiligenceEffectiveDate
                ? (moment(currentLeasehold.dueDiligenceEffectiveDate).format('YYYY-MM-DD') as unknown as Date)
                : null;
            currentLeasehold.dueDiligenceEffectiveExpirationDate = currentLeasehold.dueDiligenceEffectiveExpirationDate
                ? (moment(currentLeasehold.dueDiligenceEffectiveExpirationDate).format('YYYY-MM-DD') as unknown as Date)
                : null;
            currentLeasehold.constructionEffectiveDate = currentLeasehold.constructionEffectiveDate
                ? (moment(currentLeasehold.constructionEffectiveDate).format('YYYY-MM-DD') as unknown as Date)
                : null;
            currentLeasehold.operationEffectiveDate = currentLeasehold.operationEffectiveDate
                ? (moment(currentLeasehold.operationEffectiveDate).format('YYYY-MM-DD') as unknown as Date)
                : null;
            currentLeasehold.operationExtensionEffectiveDate = currentLeasehold.operationExtensionEffectiveDate
                ? (moment(currentLeasehold.operationExtensionEffectiveDate).format('YYYY-MM-DD') as unknown as Date)
                : null;
            currentLeasehold.optionEffectiveDate = currentLeasehold.optionEffectiveDate
                ? (moment(currentLeasehold.optionEffectiveDate).format('YYYY-MM-DD') as unknown as Date)
                : null;

            data.currentHold = currentLeasehold;
        }

        if (!!data?.addConsiderationAmount && !!data?.addConsiderationExplanation) {
            data.isAddConsideration = true;
        }

        return data;
    };

    const prepBrineLease = (roughLeaseData: BrineLease, netAcres: number) => {
        const formData: BrineLease = {
            ...roughLeaseData,
            reminderDate: roughLeaseData.reminderDate ? (moment(roughLeaseData.reminderDate).format('YYYY-MM-DD') as unknown as Date) : null,
            createdDate: roughLeaseData.createdDate ? (moment(roughLeaseData.createdDate).format('YYYY-MM-DD') as unknown as Date) : null,
            leaseDate: roughLeaseData.leaseDate ? (moment(roughLeaseData.leaseDate).format('YYYY-MM-DD') as unknown as Date) : null,
            effectiveDate: roughLeaseData.effectiveDate ? (moment(roughLeaseData.effectiveDate).format('YYYY-MM-DD') as unknown as Date) : null,
            commitDate: roughLeaseData.commitDate ? (moment(roughLeaseData.commitDate).format('YYYY-MM-DD') as unknown as Date) : null,
            inHandDate: roughLeaseData.inHandDate ? (moment(roughLeaseData.inHandDate).format('YYYY-MM-DD') as unknown as Date) : null,
            voidDate: roughLeaseData.voidDate ? (moment(roughLeaseData.voidDate).format('YYYY-MM-DD') as unknown as Date) : null,
        };

        const currentLeasehold = roughLeaseData?.brineLeaseHolds[0];

        if (currentLeasehold) {
            if (provisionLeaseClauses?.length > 0) {
                currentLeasehold.provisionLeaseClauses = [];
                for (let clause of provisionLeaseClauses) {
                    let aClause = attachedProvisionClauses?.find(ac => ac.provisionLeaseClauseId === clause.id);
                    let pClause: ProvisionClauseLeaseholdXRef = {
                        id: aClause ? aClause.id : 0,
                        provisionLeaseClauseId: clause.id,
                        contractLookupId: aClause ? aClause.contractLookupId : currentLeasehold.contractLookupId,
                        description: aClause ? aClause.description : '',
                        isSelected: aClause ? aClause.isSelected : null
                    };
                    currentLeasehold.provisionLeaseClauses.push(pClause);
                }
            }
        }

        return formData;
    };

    const initialValuesNewLease = () => {
        const newContractData = { id: 0, tracts: ownerData.tracts, legalDescription: '' };

        if (ownerData?.tracts?.length < 1) {
            newContractData.legalDescription = ' '.repeat(1001);
            setMoveLegalDescription(newContractData.legalDescription);
        }

        setIsNewContract(true);
        setLeaseTractList(ownerData.tracts);
        return newContractData;
    };

    const useDefaultLessee = () => {
        if (defaultLessee && !isInHand) {
            setValue('lesseeName', defaultLessee.ownerName());
            setValue('lesseeAddressLine1', defaultLessee.address?.addressLine1);
            setValue('lesseeAddressLine2', defaultLessee.address?.addressLine2);
            setValue('lesseeCity', defaultLessee.address?.city);
            setValue('lesseeState', defaultLessee.address?.state?.name);
            setValue('lesseeZipCode', defaultLessee.address?.zipCode);
            setValue('lesseeOwnerLookupId', defaultLessee.ownerLookUpId);
        }
    };

    const updateOwnerData = () => {
        if (!!ownerData?.ownerDetails && !isInHand) {
            const ownerInfo = OwnerUtil.MapOwner(ownerData.ownerDetails);
            setValue('ownersOnDocument', ownerInfo.ownerName());
            setValue('ownersSSNTaxIdOnDocument', ownerInfo.ssnTaxId);
            if (ownerInfo instanceof OwnerJointTenant) {
                setValue('ownersOnDocument', ownerInfo.ownerName());
                setValue(
                    'ownersAddressOnDocument',
                    `${ownerInfo?.addressLine1 ? ownerInfo?.addressLine1 + ', ' : ''}${ownerInfo?.city ? ownerInfo?.city + ', ' : ''}${ownerInfo?.state ? ownerInfo?.state + ', ' : ''
                    }${ownerInfo?.zipCode ? ownerInfo?.zipCode : ''}`
                );
            } else if (ownerInfo instanceof OwnerIndividual) {
                setValue(
                    'ownersOnDocument',
                    `${ownerInfo?.firstName}${ownerInfo?.middleName ? ` ${ownerInfo?.middleName} ` : ' '}${ownerInfo?.lastName}`
                );
                setValue(
                    'ownersAddressOnDocument',
                    `${ownerInfo.address?.addressLine1 ? ownerInfo.address?.addressLine1 + ', ' : ''}${ownerInfo.address?.city ? ownerInfo.address?.city + ', ' : ''
                    }${ownerInfo.address?.state?.name ? ownerInfo.address?.state?.name + ', ' : ''}${ownerInfo.address?.zipCode ? ownerInfo.address?.zipCode : ''
                    }`
                );
            } else {
                setValue('ownersOnDocument', ownerInfo.ownerName());
                setValue(
                    'ownersAddressOnDocument',
                    `${ownerInfo.address?.addressLine1 ? ownerInfo.address?.addressLine1 + ', ' : ''}${ownerInfo.address?.city ? ownerInfo.address?.city + ', ' : ''
                    }${ownerInfo.address?.state?.name ? ownerInfo.address?.state?.name + ', ' : ''}${ownerInfo.address?.zipCode ? ownerInfo.address?.zipCode : ''
                    }`
                );
            }
        }
    };

    const updateLegalDescription = () => {
        let tractList: Array<ContractGenerationTractDetail> = getValues().tracts;

        if (!!tractList) {
            let legal = AgreementUtil.formatTractListLegal(tractList);
            setValue('rteLegalDescription', legal);
            setMoveLegalDescription(legal);
            setDeltasAdded(true);

            let acreages = AgreementUtil.calcTotalAcreages(tractList, project?.currentProject?.landSystemId);
            setTotalGross(acreages[0]);
            setTotalNet(acreages[1]);

            setTractsSelected(!!tractList.find(tl => tl.includeTract === true));
        } else {
            console.log('Error getting tract list form:', getValues());
        }
    };

    const calculateTotalBonusPaid = (bonusPaidTypeId: number, bonusPaid: number, netAcres?: number) => {
        let bonusPaidType = measurementTypes.find((mt) => mt.id === bonusPaidTypeId)?.name;
        let net = !!netAcres ? netAcres : totalNet;
        if (!!bonusPaidType) {
            return MathUtil.cleanNumber(bonusPaidType === 'Per Item' ? bonusPaid * net : bonusPaid, 5, 3);
        }
        return '0';
    };

    const addAmendment = () => {
        if (invoiceableLookupId > 0) {
            setAmendments(
                amendments.concat([
                    {
                        id: 0,
                        invoiceableLookupId: invoiceableLookupId,
                        createdDate: null,
                        createdByUserId: 0,
                        amendmentType: '',
                        amendedField: '',
                        oldValue: '',
                        newValue: '',
                        isRecorded: false,
                        recordedDate: new Date(),
                        book: '',
                        page: '',
                        instrumentNumber: '',
                        note: '',
                    },
                ])
            );
        }
    };

    // Function to get the correct counties of the tracts wihout duplicates
    const addressRendering = (t: Array<ContractGenerationTractDetail>) => {
        let tracts: ContractGenerationTractDetail[] = t;
        let tractCounties: string[] = [];

        for (let i = 0; i < tracts?.length; ++i) {
            let countyExist: string = tractCounties?.find((c) => c === tracts[i]?.county?.name);

            if (tractCounties.length === 0 && !countyExist) {
                tractCounties.push(tracts[i]?.county?.name);
                setTractState(tracts[i]?.county?.stateId);
                continue;
            }

            if (!!countyExist && !!countyExist.includes(tracts[i]?.county?.name)) {
                continue;
            } else {
                tractCounties.push(tracts[i]?.county?.name);
            }
        }

        setCounties(tractCounties);
    };

    const tractLocationInfo = () => {
        let stateName: State = !!states ? states?.items?.find((s) => s.id === tractState) : undefined;

        if (!!counties && !!stateName) {
            let tractLocation: LocationModel = {
                counties: counties,
                state: stateName?.name,
            };

            setLocation(tractLocation);
        }
    };

    const onCommit = (data: boolean) => {
        if (!!data) {
            let formData = getValues();
            formData.commitDate = new Date().toISOString().split('T')[0];
            formData.isCommitted = true;
            onSubmit(formData as IContract);
        }
    };

    const onDecommit = () => {
        let formData = getValues();
        formData.commitDate = null;
        formData.isCommitted = false;
        onSubmit(formData as Lease);
        hideDialog();
    };

    const onInHand = () => {
        let formData = getValues();
        if (formData.ownersAddressOnDocument?.length > 0 && formData.ownersOnDocument) {
            formData.inHandDate = new Date().toISOString().split('T')[0];
            formData.isInHand = true;
            if (formData?.tracts?.length > 0) {
                formData?.tracts?.forEach((t: ContractGenerationTractDetail) => {
                    if (t.includeTract === true) {
                        t.leasedStatus = 'Leased';
                    }
                });
            }
            onSubmit(formData as IContract);
        } else {
            window.alert('Owner Information required for In-Handing!');
        }
    };

    const updateProcessing = (current: number, selection: Array<number>) => {
        setSelectedClauses(selection);

        // setValue('leasehold.leaseholdClause.', ownerInfo.ownerName());
    };

    const onSubmit = (data: IContract) => {
        console.log('formvalues', data);
        setFormLoading(true);
        if (tractsSelected === true || ownerData?.tracts?.length < 1) {
            data = finalizeData(data);

            switch (contractType) {
                case LandContractType.OilAndGas:
                    // let oilAndGasLease = data as Lease;
                    // handleLeaseholdClauses(oilAndGasLease.leaseholds[0].provisionLeaseClauses);
                    break;
                case LandContractType.BrineLease:
                    let brineLease = data as BrineLease;
                    handleLeaseholdClauses(brineLease.brineLeaseHolds[0].provisionLeaseClauses);
                    break;
                case LandContractType.SurfaceWaiver:
                case LandContractType.SolarLease:
                case LandContractType.BatteryStorage:
                default:
                    break;
            }

            if (contractId > 0) {
                updateContract(data);
            } else {
                data.isGeneratedLease = true;
                createContract(data);
            }
        } else {
            window.alert('No Tract(s) Selected');
        }
    };

    const handleLeaseholdClauses = (provisionClauses: Array<ProvisionClauseLeaseholdXRef>) => {
        let deleteClauseIds: number[] = [];
        let addClauses: ProvisionClauseLeaseholdXRef[] = [];
        let updateClauses: ProvisionClauseLeaseholdXRef[] = [];

        for (let clause of provisionClauses) {
            if (clause.id > 0 && clause.isSelected === null) {
                deleteClauseIds.push(clause.provisionLeaseClauseId);
            } else if (clause.id === 0 && clause.isSelected !== null) {
                addClauses.push(clause);
            } else if (clause.id > 0) {
                updateClauses.push(clause);
            }
        }

        if (deleteClauseIds.length > 0) {
            deleteLeaseholdClauses(deleteClauseIds);
        }
        if (addClauses.length > 0) {
            addLeaseholdClauses(addClauses);
        }
        if (updateClauses.length > 0) {
            updateLeaseholdClauses(updateClauses);
        }
    };

    const refreshPage = (listener: boolean) => {
        if (listener === true) {
            getContractData();
        }
    };

    const handleSuccessfulSubmit = (id?: number) => {
        getContractClauses();
        setOriginalTractList([]);
        if (id === contractId) {
            getContractData();
        } else {
            setContractId(id);
        }
    };

    const logContractInformation = (updateData: IContract) => {
        let contractTuple: { item1: IContract, item2: IContract };
        contractTuple = { item1: contractData, item2: updateData };
        let logBody = JSON.stringify(contractTuple);

        post(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${ownershipId}/${controllerPath}/modification-log`, logBody);

        if (response.ok) {
            console.log('Contract Logging, model binding success');
        }
    };
    
    const displayClauses = () => {
        let clauseRows: Array<Array<IndexReferenceType>> = [[]];
        let rowIndex = 0;

        for (let i = 0; i < provisionLeaseClauses.length; i++) {
            if (clauseRows[rowIndex].length < 8) {
                clauseRows[rowIndex].push(provisionLeaseClauses[i]);
            } else {
                clauseRows.push([]);
                rowIndex++;
            }
        }

        return clauseRows.map((cr, index) => {
            return (
                <div className="form-row" key={`provisionClause-${index + 1}`}>
                    {cr.map((pc: IndexReferenceType, rIndex: number) => {
                        let currentIndex = (index * 8) + (rIndex);
                        return (
                            <ProvisionClause
                                label={pc.name}
                                formPrefix={`brineLeaseHolds[0].provisionLeaseClauses[${currentIndex}]`}
                                descriptionRequired={true}
                                register={register}
                                errors={errors}
                                key={`provisionClause-${index}.${rIndex + 1}`}
                                style={{ width: '20rem' }}
                            />
                        );
                    })}
                </div>
            );
        });
    };

    // FORMAT FORM DATA
    const finalizeData = (data: IContract) => {
        if (data.contractAmendments?.length > 0) {
            data.contractAmendments.forEach((amendment: ContractAmendment) => {
                amendment.invoiceableLookupId = invoiceableLookupId;
                amendment.id = amendment.id ? parseInt(amendment.id + '', 10) : 0;
                amendment.createdByUserId = amendment.createdByUserId ? parseInt(amendment.createdByUserId + '', 10) : 0;

                amendment.amendmentType = amendment.amendmentType ? amendment.amendmentType : '';
                amendment.amendedField = amendment.amendedField ? amendment.amendedField : '';
                amendment.oldValue = amendment.oldValue ? amendment.oldValue : '';
                amendment.newValue = amendment.newValue ? amendment.newValue : '';
                amendment.book = amendment.book ? amendment.book : '';
                amendment.page = amendment.page ? amendment.page : '';
                amendment.instrumentNumber = amendment.instrumentNumber ? amendment.instrumentNumber : '';
                amendment.note = amendment.note ? amendment.note : '';

                amendment.isRecorded = amendment.isRecorded ? amendment.isRecorded : false;

                amendment.createdDate = amendment.createdDate ? (moment(amendment.createdDate).format('YYYY-MM-DD') as unknown as Date) : null;
                amendment.recordedDate = amendment.recordedDate ? (moment(amendment.recordedDate).format('YYYY-MM-DD') as unknown as Date) : null;

                if (amendment.id === 0 && amendment.amendedField.length > 0) {
                    data[amendment.amendedField] = amendment.newValue;
                }
            });
        }

        switch (contractType) {
            case LandContractType.SurfaceWaiver:
                data.id = appContext.surfaceWaiverId > 0 ? appContext.surfaceWaiverId : 0;
                data = finalizeSurfaceWaiverData(data as SurfaceWaiver);
                break;
            case LandContractType.SolarLease:
                data.id = appContext.solarLeaseId > 0 ? appContext.solarLeaseId : 0;
                data = finalizeSolarLeaseData(data as SolarLease);
                break;
            case LandContractType.BatteryStorage:
                data.id = appContext.batteryStorageId > 0 ? appContext.batteryStorageId : 0;
                data = finalizeBatteryStorageData(data as BatteryStorage);
                break;
            case LandContractType.OilAndGas:
                data.id = appContext.leaseId > 0 ? appContext.leaseId : 0;
                // data = finalizeOilAndGas(data as Lease);
                break;
            case LandContractType.BrineLease:
                data.id = appContext.brineLeaseId > 0 ? appContext.brineLeaseId : 0;
                data = finalizeBrineLease(data as BrineLease);
                break;
            default:
                break;
        }

        data.removeClauses = [];

        if (data.adHocClauses?.length > 0) {
            data.adHocClauses?.map((ahc, index) => {
                ahc.id = ahc.id ? parseInt(ahc.id + '', 10) : null;
                ahc.contractLookupId = ahc.contractLookupId ? parseInt(ahc.contractLookupId + '', 10) : null;
                ahc.leaseId = null;
                ahc.leaseClauseTypeId = ahc.leaseClauseTypeId ? parseInt(ahc.leaseClauseTypeId + '', 10) : null;
                ahc.clauseText = ahc.clauseText ? ahc.clauseText : '';
            });
            for (let clause of contractData?.adHocClauses) {
                if (data.adHocClauses?.indexOf(data.adHocClauses?.find(ahc => ahc.id === clause.id)) === -1) {
                    data.removeClauses.push(clause.id);
                }
            }
        } else {
            data.removeClauses = contractData?.adHocClauses?.length > 0 ? contractData.adHocClauses.map(cd => cd.id) : [];
        }

        if (!!data.contractClauses) {
            let contractClause: Array<ContractAddendumClauseXRef> = data?.contractClauses?.map((cac: ContractAddendumClauseXRef) => {
                let formatted = {
                    invoiceableLookupId: invoiceableLookupId,
                    contractAddendumClauseId: cac.contractAddendumClauseId ? parseInt(cac.contractAddendumClauseId + '', 10) : 0,
                    id: 0,
                };

                formatted.id =
                    contractData?.contractClauses?.find(
                        (cc) =>
                            cc.invoiceableLookupId === formatted.invoiceableLookupId && cc.contractAddendumClauseId === formatted.contractAddendumClauseId
                    ) !== undefined
                        ? contractData.contractClauses.find(
                            (cc) =>
                                cc.invoiceableLookupId === formatted.invoiceableLookupId &&
                                cc.contractAddendumClauseId === formatted.contractAddendumClauseId
                        ).id
                        : 0;

                return formatted;
            });

            let filteredClause: Array<ContractAddendumClauseXRef> = contractClause.filter((lc) => lc.contractAddendumClauseId > 0);
            data.contractClauses = filteredClause;
        } else {
            data.contractClauses = [];
        }

        data.effectiveDate = data.effectiveDate ? (moment(data.effectiveDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.commitDate = data.commitDate ? (moment(data.commitDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.inHandDate = data.inHandDate ? (moment(data.inHandDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.createdDate = moment(new Date().toISOString().split('T')[0]).format('YYYY-MM-DD') as unknown as Date;
        data.reminderDate = data.reminderDate ? (moment(data.reminderDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.recordedDate = data.recordedDate ? (moment(data.recordedDate).format('YYYY-MM-DD') as unknown as Date) : null;

        // Generated will always be true and filed by memo will always be false.
        data.isFiledByMemo = false;
        data.isGeneratedLease = true;
        data.isVoid = data.isVoid ? data.isVoid : false;
        data.isNotOriginalLessee = data.isNotOriginalLessee ? data.isNotOriginalLessee : false;
        if (!!contractData) {
            data.isCommitted = (data.isCommitted !== contractData.isCommitted && data.isCommitted !== undefined) ? !contractData.isCommitted : contractData.isCommitted;
            data.isInHand = (data.isInHand !== contractData.isInHand && data.isInHand !== undefined) ? !contractData.isInHand : contractData.isInHand;
        } else {
            data.isCommitted = data.isCommitted ? true : false;
            data.isInHand = data.isInHand ? true : false;
        }
        data.legalDescription = data.legalDescription ? data.legalDescription : '';
        data.leasedBy = data.leasedBy ? data.leasedBy : '';
        data.originalLessee = data.originalLessee ? data.originalLessee : '';
        data.leaseLessorNotes = data.leaseLessorNotes ? data.leaseLessorNotes : '';
        data.ownersOnDocument = data.ownersOnDocument ? data.ownersOnDocument : '';
        data.ownersAddressOnDocument = data.ownersAddressOnDocument ? data.ownersAddressOnDocument : '';
        data.lesseeName = data.lesseeName ? data.lesseeName : '';
        data.lesseeAddressLine1 = data.lesseeAddressLine1 ? data.lesseeAddressLine1 : '';
        data.lesseeAddressLine2 = data.lesseeAddressLine2 ? data.lesseeAddressLine2 : '';
        data.lesseeCity = data.lesseeCity ? data.lesseeCity : '';
        data.lesseeState = data.lesseeState ? data.lesseeState : '';
        data.lesseeZipCode = data.lesseeZipCode ? data.lesseeZipCode : '';
        data.lesseePhoneNumber = data.lesseePhoneNumber ? data.lesseePhoneNumber : '';
        data.legalDescription = data.rteLegalDescription ? AgreementUtil.convertToPlainText(data.rteLegalDescription) : data.legalDescription;

        // TODO: Consider a better solution than defaulting to this lessee if there's no data.
        data.lesseeOwnerLookupId = data.lesseeOwnerLookupId ? parseInt(data.lesseeOwnerLookupId + '', 10) : defaultLessee.ownerLookUpId;
        
        data.reminderNote = data.reminderNote ? data.reminderNote : '';
        data.bonusAmount = data.bonusAmount ? parseFloat(data.bonusAmount + '') : null;
        data.bonusTypeId = data.bonusTypeId ? parseInt(data.bonusTypeId + '', 10) : null;

        if (data.tracts?.length > 0) {
            data.tracts.forEach((tract: ContractGenerationTractDetail) => {
                tract.id = tract.id ? parseInt(tract.id + '', 10) : 0;
                tract.tractNumber = tract.tractNumber ? parseInt(tract.tractNumber + '', 10) : 0;
                tract.surfaceTractNumber = tract.surfaceTractNumber ? parseInt(tract.surfaceTractNumber + '', 10) : 0;
                tract.contractLookupId = tract.contractLookupId ? parseInt(tract.contractLookupId + '', 10) : null;
                tract.invoiceableLookupId = invoiceableLookupId;
                tract.section = tract.section ? parseInt(tract.section + '', 10) : 0;
                tract.sectionNumber = tract.sectionNumber ? parseInt(tract.sectionNumber + '', 10) : 0;
                tract.horizonNumber = tract.horizonNumber ? parseInt(tract.horizonNumber + '', 10) : 0;
                tract.countyId = tract.countyId ? parseInt(tract.countyId + '', 10) : null;
                tract.grossAcres = tract.grossAcres ? parseFloat(tract.grossAcres + '') : 0;
                tract.includeTract = tract.includeTract ? true : false;
                tract.ownershipId = tract.ownershipId ? parseInt(tract.ownershipId + '', 10) : null;
                tract.contractLookupId = tract.contractLookupId ? parseInt(tract.contractLookupId + '', 10) : null;
                tract.netAcres = tract.netAcres ? parseFloat(tract.netAcres + '') : 0;
                tract.hasNEMI = tract.hasNEMI ? true : false;
                tract.decimalInterest = tract.decimalInterest ? parseFloat(tract.decimalInterest + '') : 0;
                tract.type = tract.type ? tract.type : '';
                tract.legalDescription = tract.legalDescription ? tract.legalDescription : '';
                tract.township = tract.township;
            });
        }

        return data;
    };

    // BATTERY STORAGE FORMATTING
    const finalizeBatteryStorageData = (data: BatteryStorage) => {
        data.batteryStorageHolds = data.batteryStorageHolds.map((rh: BatteryStorageHold) => {
            return finalizeBatteryStorageHold(rh);
        });

        data.recordDate = data.recordDate ? (moment(data.recordDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.leaseDate = data.leaseDate ? (moment(data.leaseDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.voidDate = data.voidDate ? (moment(data.voidDate).format('YYYY-MM-DD') as unknown as Date) : null;

        data.contractName = data.contractName ? data.contractName : '';
        data.volume = data.volume ? data.volume : '';
        data.page = data.page ? data.page : '';
        data.instrumentNumber = data.instrumentNumber ? data.instrumentNumber : '';
        data.fedStateLeaseNumber = data.fedStateLeaseNumber ? data.fedStateLeaseNumber : '';
        data.lessorAka = data.lessorAka ? data.lessorAka : '';
        data.legalDescription = data.legalDescription ? data.legalDescription : '';

        data.originalGrossAcres = totalGross ? totalGross : 0;
        data.dlcLeaseNumber = leaseNumber ? leaseNumber : 0;

        data.recordTypeId = data.recordTypeId ? parseInt(data.recordTypeId + '', 10) : null;
        data.createdByUserId = data.createdByUserId ? parseInt(data.createdByUserId + '', 10) : null;
        data.leasedByUserId = data.leasedByUserId ? parseInt(data.leasedByUserId + '', 10) : null;
        data.lesseeOwnerLookupId = data.lesseeOwnerLookupId ? parseInt(data.lesseeOwnerLookupId + '', 10) : null;

        return data;
    };

    const finalizeBatteryStorageHold = (data: BatteryStorageHold) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.batteryStorageId = appContext.batteryStorageId ? appContext.batteryStorageId : 0;

        data.executionBonus = data.executionBonus ? parseFloat(data.executionBonus + '') : 0;
        data.constructionFee = data.constructionFee ? parseFloat(data.constructionFee + '') : 0;
        data.constructionExtensionFee = data.constructionExtensionFee ? parseFloat(data.constructionExtensionFee + '') : 0;
        data.accessFee = data.accessFee ? parseFloat(data.accessFee + '') : 0;
        data.accessWidth = data.accessWidth ? parseFloat(data.accessWidth + '') : 0;
        data.transmissionFee = data.transmissionFee ? parseFloat(data.transmissionFee + '') : 0;
        data.transmissionWidth = data.transmissionWidth ? parseFloat(data.transmissionWidth + '') : 0;
        data.temporaryUseFee = data.temporaryUseFee ? parseFloat(data.temporaryUseFee + '') : 0;
        data.developmentRentAmount = data.developmentRentAmount ? parseFloat(data.developmentRentAmount + '') : 0;
        data.firstExtensionOptionRentAmount = data.firstExtensionOptionRentAmount ? parseFloat(data.firstExtensionOptionRentAmount + '') : 0;
        data.secondExtensionOptionRentAmount = data.secondExtensionOptionRentAmount ? parseFloat(data.secondExtensionOptionRentAmount + '') : 0;
        data.operationsBaseRentAmount = data.operationsBaseRentAmount ? parseFloat(data.operationsBaseRentAmount + '') : 0;
        data.annualEscalationRate = data.annualEscalationRate ? parseFloat(data.annualEscalationRate + '') : 0;
        data.overdueRateTerm = data.overdueRateTerm ? parseFloat(data.overdueRateTerm + '') : 0;
        data.extendedOperationsRentAmount = data.extendedOperationsRentAmount ? parseFloat(data.extendedOperationsRentAmount + '') : 0;
        data.restorationTermRentAmount = data.restorationTermRentAmount ? parseFloat(data.restorationTermRentAmount + '') : 0;
        data.additionalPayments = data.additionalPayments ? parseFloat(data.additionalPayments + '') : 0;
        data.cropBuyoutFee = data.cropBuyoutFee ? parseFloat(data.cropBuyoutFee + '') : 0;
        data.substationFee = data.substationArea ? parseFloat(data.substationFee + '') : 0;
        data.substationAcres = data.substationAcres ? parseFloat(data.substationAcres + '') : 0;

        data.batteryStorageHoldNumber = data.batteryStorageHoldNumber ? parseInt(data.batteryStorageHoldNumber + '', 10) : 0;
        data.executionBonusTypeId = data.executionBonusTypeId ? parseInt(data.executionBonusTypeId + '', 10) : null;
        data.constructionFeeTypeId = data.constructionFeeTypeId ? parseInt(data.constructionFeeTypeId + '', 10) : null;
        data.constructionExtensionFeeTypeId = data.constructionExtensionFeeTypeId ? parseInt(data.constructionExtensionFeeTypeId + '', 10) : null;
        data.accessFeeTypeId = data.accessFeeTypeId ? parseInt(data.accessFeeTypeId + '', 10) : null;
        data.accessWidthTypeId = data.accessWidthTypeId ? parseInt(data.accessWidthTypeId + '', 10) : null;
        data.transmissionFeeTypeId = data.transmissionFeeTypeId ? parseInt(data.transmissionFeeTypeId + '', 10) : null;
        data.transmissionWidthTypeId = data.transmissionWidthTypeId ? parseInt(data.transmissionWidthTypeId + '', 10) : null;
        data.temporaryUseFeeTypeId = data.temporaryUseFeeTypeId ? parseInt(data.temporaryUseFeeTypeId + '', 10) : null;
        data.cropBuyoutFeeTypeId = data.cropBuyoutFeeTypeId ? parseInt(data.cropBuyoutFeeTypeId + '', 10) : null;
        data.developmentPeriodTerm = data.developmentPeriodTerm ? parseInt(data.developmentPeriodTerm + '', 10) : 0;
        data.developmentPeriodTermTypeId = data.developmentPeriodTermTypeId ? parseInt(data.developmentPeriodTermTypeId + '', 10) : null;
        data.developmentRentTermTypeId = data.developmentRentTermTypeId ? parseInt(data.developmentRentTermTypeId + '', 10) : null;
        data.developmentPeriodExtensionTerm = data.developmentPeriodExtensionTerm ? parseInt(data.developmentPeriodExtensionTerm + '', 10) : 0;
        data.developmentPeriodExtensionTermTypeId = data.developmentPeriodExtensionTermTypeId
            ? parseInt(data.developmentPeriodExtensionTermTypeId + '', 10)
            : null;
        data.firstExtensionOptionRentTermTypeId = data.firstExtensionOptionRentTermTypeId
            ? parseInt(data.firstExtensionOptionRentTermTypeId + '', 10)
            : null;
        data.developmentPeriodSecondExtensionTerm = data.developmentPeriodSecondExtensionTerm
            ? parseInt(data.developmentPeriodSecondExtensionTerm + '', 10)
            : 0;
        data.developmentPeriodSecondExtensionTermTypeId = data.developmentPeriodSecondExtensionTermTypeId
            ? parseInt(data.developmentPeriodSecondExtensionTermTypeId + '', 10)
            : null;
        data.secondExtensionOptionRentTermTypeId = data.secondExtensionOptionRentTermTypeId
            ? parseInt(data.secondExtensionOptionRentTermTypeId + '', 10)
            : null;
        data.operationsPeriodTerm = data.operationsPeriodTerm ? parseInt(data.operationsPeriodTerm + '', 10) : 0;
        data.operationsPeriodTermTypeId = data.operationsPeriodTermTypeId ? parseInt(data.operationsPeriodTermTypeId + '', 10) : null;
        data.operationsBaseRentTermTypeId = data.operationsBaseRentTermTypeId ? parseInt(data.operationsBaseRentTermTypeId + '', 10) : null;
        data.extendedOperationsPeriodTerm = data.extendedOperationsPeriodTerm ? parseInt(data.extendedOperationsPeriodTerm + '', 10) : 0;
        data.extendedOperationsPeriodTermTypeId = data.extendedOperationsPeriodTermTypeId
            ? parseInt(data.extendedOperationsPeriodTermTypeId + '', 10)
            : null;
        data.extendedOperationsRentTermTypeId = data.extendedOperationsRentTermTypeId
            ? parseInt(data.extendedOperationsRentTermTypeId + '', 10)
            : null;
        data.restorationTerm = data.restorationTerm ? parseInt(data.restorationTerm + '', 10) : 0;
        data.restorationTermTypeId = data.restorationTermTypeId ? parseInt(data.restorationTermTypeId + '', 10) : null;
        data.restorationTermRentTypeId = data.restorationTermRentTypeId ? parseInt(data.restorationTermRentTypeId + '', 10) : null;
        data.substationFeeTypeId = data.substationFeeTypeId ? parseInt(data.substationFeeTypeId + '', 10) : null;

        data.accessDetails = data.accessDetails ? data.accessDetails : '';
        data.transmissionDetails = data.transmissionDetails ? data.transmissionDetails : '';
        data.temporaryUseDetails = data.temporaryUseDetails ? data.temporaryUseDetails : '';
        data.cropBuyoutDetails = data.cropBuyoutDetails ? data.cropBuyoutDetails : '';

        data.accessEasement = data.accessEasement ? true : false;
        data.transmissionEasement = data.transmissionEasement ? true : false;
        data.temporaryUseAreas = data.temporaryUseAreas ? true : false;
        data.developmentPeriod = data.developmentPeriod ? true : false;
        data.developmentPeriodExtension = data.developmentPeriodExtension ? true : false;
        data.developmentPeriodSecondExtension = data.developmentPeriodSecondExtension ? true : false;
        data.operationsPeriod = data.operationsPeriod ? true : false;
        data.additionalExtensionOptions = data.additionalExtensionOptions ? true : false;
        data.escalationAbovePrime = data.escalationAbovePrime ? true : false;
        data.overdueAbovePrime = data.overdueAbovePrime ? true : false;
        data.isActive = true;
        data.isHistorical = data.isHistorical ? data.isHistorical : null;
        data.cropBuyout = data.cropBuyout ? true : false;
        data.substationArea = data.substationArea ? true : false;
        data.constructionPeriod = data.constructionPeriod ? true : false;
        data.constructionExtensionPeriod = data.constructionExtensionPeriod ? true : false;
        data.prepaidOption = data.prepaidOption ? true : false;

        return data;
    };

    // Surface Waiver
    const finalizeSurfaceWaiverData = (data: SurfaceWaiver) => {
        data.surfaceWaiverNumber = leaseNumber ? leaseNumber : 0;

        data.surfaceWaiverHolds = data.surfaceWaiverHolds.map((rh: SurfaceWaiverHold) => {
            return finalizeSurfaceWaiverHold(rh);
        });

        data.recordDate = data.recordDate ? (moment(data.recordDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.contractDate = data.contractDate ? (moment(data.contractDate).format('YYYY-MM-DD') as unknown as Date) : null;

        data.name = data.name ? data.name : '';
        data.grantorAka = data.grantorAka ? data.grantorAka : '';
        data.spouseName = data.spouseName ? data.spouseName : '';
        data.spouseAkaName = data.spouseAkaName ? data.spouseAkaName : '';
        data.maritalStatus = data.maritalStatus ? data.maritalStatus : '';
        data.fedStateLeaseNumber = data.fedStateLeaseNumber ? data.fedStateLeaseNumber : '';
        data.volume = data.volume ? data.volume : '';
        data.page = data.page ? data.page : '';
        data.instrumentNumber = data.instrumentNumber ? data.instrumentNumber : '';

        data.grossAcres = totalGross ? totalGross : 0;

        data.recordTypeId = data.recordTypeId ? parseInt(data.recordTypeId + '', 10) : null;
        data.createdByUserId = data.createdByUserId ? parseInt(data.createdByUserId + '', 10) : null;
        data.leasedByUserId = data.leasedByUserId ? parseInt(data.leasedByUserId + '', 10) : null;

        return data;
    };

    // Surface Waiver Hold
    const finalizeSurfaceWaiverHold = (data: SurfaceWaiverHold) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.surfaceWaiverId = appContext.surfaceWaiverId ? appContext.surfaceWaiverId : 0;

        data.waiverDate = data.waiverDate ? (moment(data.waiverDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.expirationDate = data.expirationDate ? (moment(data.expirationDate).format('YYYY-MM-DD') as unknown as Date) : null;

        data.bonusPaid = data.bonusPaid ? parseFloat(data.bonusPaid + '') : 0;
        data.additionalConsideration = data.additionalConsideration ? parseFloat(data.additionalConsideration + '') : 0;
        data.totalBonusPaid = data.totalBonusPaid ? parseFloat(data.totalBonusPaid + '') : 0;

        data.surfaceWaiverHoldNumber = data.surfaceWaiverHoldNumber ? parseInt(data.surfaceWaiverHoldNumber + '', 10) : 0;
        data.primaryTerm = data.primaryTerm ? parseInt(data.primaryTerm + '', 10) : 0;
        data.primaryTermTypeId = data.primaryTermTypeId ? parseInt(data.primaryTermTypeId + '', 10) : null;
        data.bonusPaidTypeId = data.bonusPaidTypeId ? parseInt(data.bonusPaidTypeId + '', 10) : null;

        data.note = data.note ? data.note : '';
        data.description = data.description ? data.description : '';

        data.isPerpetual = data.isPerpetual ? data.isPerpetual : null;
        data.isActive = true;
        data.isHistorical = data.isHistorical ? data.isHistorical : null;

        return data;
    };

    // Solar Lease
    const finalizeSolarLeaseData = (data: SolarLease) => {
        if (data.currentHold) {
            data.currentHold.id = data.currentHold.id ? parseInt(data.currentHold.id + '', 10) : 0;
            data.currentHold.solarLeaseId = data.currentHold.solarLeaseId ? parseInt(data.currentHold.solarLeaseId + '', 10) : 0;
            data.currentHold.ownershipId = data.currentHold.ownershipId ? parseInt(data.currentHold.ownershipId + '', 10) : null;

            data.currentHold.dueDiligenceEffectiveDate = data.currentHold.dueDiligenceEffectiveDate
                ? (moment(data.currentHold.dueDiligenceEffectiveDate).format('YYYY-MM-DD') as unknown as Date)
                : null;
            data.currentHold.rentDueDate = data.currentHold.rentDueDate
                ? (moment(data.currentHold.rentDueDate).format('YYYY-MM-DD') as unknown as Date)
                : null;
            data.currentHold.dueDiligenceEffectiveTerm = data.currentHold.dueDiligenceEffectiveTerm
                ? parseInt(data.currentHold.dueDiligenceEffectiveTerm + '', 10)
                : 0;
            data.currentHold.dueDiligenceEffectiveTermTypeId = data.currentHold.dueDiligenceEffectiveTermTypeId
                ? parseInt(data.currentHold.dueDiligenceEffectiveTermTypeId + '', 10)
                : null;
            data.currentHold.optionEffectiveDate = data.currentHold.optionEffectiveDate
                ? (moment(data.currentHold.optionEffectiveDate).format('YYYY-MM-DD') as unknown as Date)
                : null;
            data.currentHold.optionTerm = data.currentHold.optionTerm ? parseInt(data.currentHold.optionTerm + '', 10) : 0;
            data.currentHold.optionTermTypeId = data.currentHold.optionTermTypeId ? parseInt(data.currentHold.optionTermTypeId + '', 10) : null;
            data.currentHold.constructionEffectiveDate = data.currentHold.constructionEffectiveDate
                ? (moment(data.currentHold.constructionEffectiveDate).format('YYYY-MM-DD') as unknown as Date)
                : null;
            data.currentHold.constructionTerm = data.currentHold.constructionTerm ? parseInt(data.currentHold.constructionTerm + '', 10) : 0;
            data.currentHold.constructionTermTypeId = data.currentHold.constructionTermTypeId
                ? parseInt(data.currentHold.constructionTermTypeId + '', 10)
                : null;
            data.currentHold.operationEffectiveDate = data.currentHold.operationEffectiveDate
                ? (moment(data.currentHold.operationEffectiveDate).format('YYYY-MM-DD') as unknown as Date)
                : null;
            data.currentHold.operationTerm = data.currentHold.operationTerm ? parseInt(data.currentHold.operationTerm + '', 10) : 0;
            data.currentHold.operationTermTypeId = data.currentHold.operationTermTypeId
                ? parseInt(data.currentHold.operationTermTypeId + '', 10)
                : null;
            data.currentHold.operationExtensionEffectiveDate = data.currentHold.operationExtensionEffectiveDate
                ? (moment(data.currentHold.operationExtensionEffectiveDate).format('YYYY-MM-DD') as unknown as Date)
                : null;
            data.currentHold.operationExtensionTerm = data.currentHold.operationExtensionTerm
                ? parseInt(data.currentHold.operationExtensionTerm + '', 10)
                : 0;
            data.currentHold.operationExtensionTermTypeId = data.currentHold.operationExtensionTermTypeId
                ? parseInt(data.currentHold.operationExtensionTermTypeId + '', 10)
                : null;
            //
            data.currentHold.dueDiligenceExtensionFee = data.currentHold.dueDiligenceExtensionFee
                ? parseFloat(data.currentHold.dueDiligenceExtensionFee + '')
                : 0;
            data.currentHold.dueDiligenceFee = data.currentHold.dueDiligenceFee ? parseFloat(data.currentHold.dueDiligenceFee + '') : 0;

            data.currentHold.dueDiligenceFeeTypeId = data.currentHold.dueDiligenceFeeTypeId
                ? parseInt(data.currentHold.dueDiligenceFeeTypeId + '', 10)
                : null;
            data.currentHold.dueDiligenceExtensionFee = data.currentHold.dueDiligenceExtensionFee
                ? parseFloat(data.currentHold.dueDiligenceExtensionFee + '')
                : 0;
            data.currentHold.dueDiligenceExtensionFeeTypeId = data.currentHold.dueDiligenceExtensionFeeTypeId
                ? parseInt(data.currentHold.dueDiligenceExtensionFeeTypeId + '', 10)
                : null;
            data.currentHold.constructionFee = data.currentHold.constructionFee ? parseFloat(data.currentHold.constructionFee + '') : 0;
            data.currentHold.constructionFeeTypeId = data.currentHold.constructionFeeTypeId
                ? parseInt(data.currentHold.constructionFeeTypeId + '', 10)
                : null;
            data.currentHold.operatingIPFFee = data.currentHold.operatingIPFFee ? parseFloat(data.currentHold.operatingIPFFee + '') : 0;
            data.currentHold.operatingIPFFeeTypeId = data.currentHold.operatingIPFFeeTypeId
                ? parseInt(data.currentHold.operatingIPFFeeTypeId + '', 10)
                : null;
            data.currentHold.operatingOPFFee = data.currentHold.operatingOPFFee ? parseFloat(data.currentHold.operatingOPFFee + '') : 0;
            data.currentHold.operatingOPFFeeTypeId = data.currentHold.operatingOPFFeeTypeId
                ? parseInt(data.currentHold.operatingOPFFeeTypeId + '', 10)
                : null;
            data.currentHold.escalatingPct = data.currentHold.escalatingPct ? parseFloat(data.currentHold.escalatingPct + '') : 0;
            data.currentHold.prepaidOption = data.currentHold.prepaidOption ? AgreementUtil.parseBool(data.currentHold.prepaidOption + '') : null;
            data.currentHold.isActive = true;

            data.solarLeaseHolds = [data.currentHold];
        }

        data.rentDueDate = data.rentDueDate ? (moment(data.rentDueDate).format('YYYY-MM-DD') as unknown as Date) : null;

        data.rentalAmount = data.rentalAmount ? parseFloat(data.rentalAmount + '') : null;
        data.rentalTypeId = data.rentalTypeId ? parseInt(data.rentalTypeId + '', 10) : null;
        data.rentalFrequencyId = data.rentalFrequencyId ? parseInt(data.rentalFrequencyId + '', 10) : null;
        data.rentalTerms = data.rentalTerms ? parseInt(data.rentalTerms + '', 10) : 0;
        data.rentalTermTypeId = data.rentalTermTypeId ? parseInt(data.rentalTermTypeId + '', 10) : null;
        data.initialBonus = data.initialBonus ? parseInt(data.initialBonus + '', 10) : 0;
        data.rentTotal = data.rentTotal ? parseInt(data.rentTotal + '', 10) : 0;

        data.totalBonus = data.totalBonus ? parseFloat(data.totalBonus + '') : null;
        data.addOptionTotalBonus = data.addOptionTotalBonus ? parseFloat(data.addOptionTotalBonus + '') : null;
        data.addOptionBonusAmount = data.addOptionBonusAmount ? parseFloat(data.addOptionBonusAmount + '') : null;
        data.addOptionTerm = data.addOptionTerm ? parseInt(data.addOptionTerm + '', 10) : null;
        data.addConsiderationAmount = data.addConsiderationAmount ? parseFloat(data.addConsiderationAmount + '') : null;
        data.totalConsideration = data.totalConsideration ? parseFloat(data.totalConsideration + '') : null;
        data.optionTotalBonus = data.optionTotalBonus ? parseFloat(data.optionTotalBonus + '') : null;
        data.clientDocNumber = data.clientDocNumber ? data.clientDocNumber : '';

        data.grossAcres = totalGross ? totalGross : 0;
        data.solarLeaseNumber = leaseNumber ? leaseNumber : 0;

        return data;
    };

    const finalizeBrineLease = (data: BrineLease) => {
        data.leaseNumber = leaseNumber ? leaseNumber : 0;

        data.volume = data.volume ? data.volume : '';
        data.page = data.page ? data.page : '';
        data.instrumentNumber = data.instrumentNumber ? data.instrumentNumber : '';
        data.originalLessor = data.originalLessor ? data.originalLessor : '';
        data.lessorAka = data.lessorAka ? data.lessorAka : '';
        
        data.leaseDate = data.leaseDate
        ? (moment(data.leaseDate).format('YYYY-MM-DD') as unknown as Date)
        : null;
        data.effectiveDate = data.effectiveDate
        ? (moment(data.effectiveDate).format('YYYY-MM-DD') as unknown as Date)
        : null;
        
        data.createdByUserId = data.createdByUserId ? parseInt(data.createdByUserId + '', 10) : null;
        
        data.originalGrossAcres = totalGross ? totalGross : null;

        if (data.brineLeaseHolds?.length > 0) {
            data.brineLeaseHolds = data.brineLeaseHolds.map((brineLeaseHold: BrineLeaseHold) => {
                return finalizeBrineLeaseHold(brineLeaseHold);
            });
        }

        return data;
    };

    const finalizeBrineLeaseHold = (data: BrineLeaseHold) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.contractLookupId = data.contractLookupId ? parseInt(data.contractLookupId + '', 10) : 0;
        data.brineLeaseId = contractData?.id ? contractData.id : 0;
        data.projectId = project.currentProject?.id;

        data.number = data.number ? parseInt(data.number + '', 10) : null;
        data.primaryRentalTerm = data.primaryRentalTerm ? parseInt(data.primaryRentalTerm + '', 10) : null;
        data.primaryUnitTypeId = data.primaryUnitTypeId ? parseInt(data.primaryUnitTypeId + '', 10) : null;
        data.primaryRentalFeeTypeId = data.primaryRentalFeeTypeId ? parseInt(data.primaryRentalFeeTypeId + '', 10) : null;
        data.optionTerm = data.optionTerm ? parseInt(data.optionTerm + '', 10) : null;
        data.optionUnitTypeId = data.optionUnitTypeId ? parseInt(data.optionUnitTypeId + '', 10) : null;
        data.optionFeeTypeId = data.optionFeeTypeId ? parseInt(data.optionFeeTypeId + '', 10) : null;
        data.extensionOptionLimit = data.extensionOptionLimit ? parseInt(data.extensionOptionLimit + '', 10) : null;

        data.primaryRentalAmount = data.primaryRentalAmount ? parseFloat(data.primaryRentalAmount + '') : null;
        data.primaryRentalTotal = data.primaryRentalTotal ? parseFloat(data.primaryRentalTotal + '') : null;
        data.optionAmount = data.optionTotal ? parseFloat(data.optionAmount + '') : null;
        data.optionTotal = data.optionTotal ? parseFloat(data.optionTotal + '') : null;
        data.royalty = data.royalty ? parseFloat(data.royalty + '') : null;
        data.isActive = true;

        data.provisionLeaseClauses.forEach((pClause: ProvisionClauseLeaseholdXRef) => {
            pClause.id = pClause.id ? parseInt(pClause.id + '', 10) : 0;
            pClause.contractLookupId = pClause.contractLookupId ? parseInt(pClause.contractLookupId + '', 10) : 0;
            pClause.provisionLeaseClauseId = pClause.provisionLeaseClauseId ? parseInt(pClause.provisionLeaseClauseId + '', 10) : 0;
            pClause.isSelected = pClause.isSelected ? AgreementUtil.parseBool(pClause.isSelected + '') : null;
        });

        data.extensionOptions?.forEach((extension) => {
            extension.id = extension.id ? parseInt(extension.id + '', 10) : 0;
            extension.optionTerm = extension.optionTerm ? parseInt(extension.optionTerm + '', 10) : null;
            extension.optionUnitTypeId = extension.optionUnitTypeId ? parseInt(extension.optionUnitTypeId + '', 10) : null;
            extension.optionFeeTypeId = extension.optionFeeTypeId ? parseInt(extension.optionFeeTypeId + '', 10) : null;
            extension.optionAmount = extension.optionTotal ? parseFloat(extension.optionAmount + '') : null;
            extension.optionTotal = extension.optionTotal ? parseFloat(extension.optionTotal + '') : null;
            extension.prepaidOption = extension.prepaidOption ? AgreementUtil.parseBool(extension.prepaidOption + '') : null;
        });

        return data;
    };

    // NAVIGATION
    const hideDialog = () => {
        setActionType(undefined);
        setPdfRenderTypeId(0);
        dialogContext.hideDialog();
    };

    const showStatusDialog = () => {
        dialogContext.contract.selectContractCommittedStatus(projectContactCategories, ownerData, contractData, onCommit, hideDialog);
    };

    const showChangeLog = (changeLogs: EntityModificationContract) => {
        let entityType: string;

        switch (contractType) {
            case LandContractType.BrineLease:
                entityType = 'Brine Lease';
                break;
            case LandContractType.OilAndGas:
            case LandContractType.SolarLease:
            case LandContractType.SurfaceWaiver:
            case LandContractType.BatteryStorage:
            default:
                break;
        }

        dialogContext.contract.getChangeLog(entityType, contractData.id, changeLogs);
    };

    const getChangeLog = async () => {
        const result: EntityModificationContract = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${ownershipId}/${controllerPath}/${contractId}/modification-log`
        );

        if (response.ok) {
            showChangeLog(result);
        }
    };

    const showLPRPDFDialog = (id: number) => {
        setPdfRenderTypeId(id);
        setActionType(DialogList.LPR_VIEWER);
    };

    const showLeasePDFDialog = async (id: number) => {
        // setPdfRenderTypeId(id);
        // setActionType(DialogList.PDF_WINDOW);

        const result = await post(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/documentTemplate/${contractId}/merge/${id}`);

        if (response.ok) {
            // window.alert('Document Generated Successfully');

            await response.blob().then((blob) => {
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.getElementById(`a_mergedDoc`) as HTMLAnchorElement;
                a.href = blobUrl;
                a.download = 'Lease';
                a.click();
                window.URL.revokeObjectURL(blobUrl);
            });
        } else {
            console.log('Unable to generate document.', result);
        }
    };

    const showMemoPDFDialog = async (id: number) => {
        // setPdfRenderTypeId(id);
        // setActionType(DialogList.MEMO_VIEWER);

        const result = await post(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/documentTemplate/${contractId}/merge/${id}`);

        if (response.ok) {
            // window.alert('Document Generated Successfully');

            await response.blob().then((blob) => {
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.getElementById(`a_mergedDoc`) as HTMLAnchorElement;
                a.href = blobUrl;
                a.download = 'Memo';
                a.click();
                window.URL.revokeObjectURL(blobUrl);
            });
        } else {
            console.log('Unable to generate document.', result);
        }
    };

    const showOopPDFDialog = async (id: number) => {
        // setPdfRenderTypeId(id);
        // setActionType(DialogList.OOP_VIEWER);

        const result = await post(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/documentTemplate/${contractId}/merge/${id}`);

        if (response.ok) {
            // window.alert('Document Generated Successfully');

            await response.blob().then((blob) => {
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.getElementById(`a_mergedDoc`) as HTMLAnchorElement;
                a.href = blobUrl;
                a.download = 'OOP';
                a.click();
                window.URL.revokeObjectURL(blobUrl);
            });
        } else {
            console.log('Unable to generate document.', result);
        }
    };

    const showUncommitConfirmationDialog = () => {
        dialogContext.contract.deCommitContract(leaseNumber.toString(), ownerData, contractData, hideDialog, onDecommit, hideDialog);
    };

    const showTractListDeltaDialog = () => {
        let oTracts = [...ownerData.tracts];

        for (let t of oTracts) {
            if (!!t.easternTownship) {
                t.township = t.easternTownship;
            }
        }

        dialogContext.owners.tractListDeltaDialog(false, project?.currentProject?.landSystemId === LandSystem.Eastern, ownerData.tracts, contractData.tracts, onDeltaUpdate);
    };

    const showConfirmRevokeInhandDialog = () => {
        dialogContext.contract.revokeInhandContractStatus(contractType, leaseNumber.toString(), hideDialog, onRevokeInHand, hideDialog);
    };

    const onDeltaUpdate = () => {
        setOriginalTractList(contractData.tracts);
        let newData = { ...contractData };
        newData.tracts = [...ownerData.tracts];
        for (let tract of newData.tracts) {
            let cLease = contractData.tracts.find(lt => lt.ownershipId === tract.ownershipId);
            tract.includeTract = !!cLease ? cLease.includeTract : false;
            tract.id = !!cLease ? cLease.id : null;
        }
        setContractData(newData);
        setDeltasAdded(true);
    };

    const scrollToSection = (ref: unknown) => {
        const formElem = document.getElementById('formContent');
        let current = 'current';
        let section = ref[current];

        if (ref === provisionSection && section?.offsetTop) {
            formElem.scrollTo({
                top: ref[current].offsetTop - 145,
                behavior: 'smooth',
            });
        } else if (section?.offsetTop) {
            formElem.scrollTo({
                top: ref[current].offsetTop - 336,
                behavior: 'smooth',
            });
        }
    };

    // const exerciseOption = () => {
    //     setValue('leasehold.optionExercised', true, { shouldDirty: true });
    //     dispatch(actions.layout.showDialog(DialogList.EXTEND_LEASEHOLDS));
    // };

    const onOwnerSelected = (lessee: IOwner) => {
        let newOwner = OwnerUtil.MapOwner(lessee);

        setValue('lesseeName', newOwner.ownerName());
        setValue('lesseeAddressLine1', newOwner.address?.addressLine1);
        setValue('lesseeAddressLine2', newOwner.address?.addressLine2);
        setValue('lesseeCity', newOwner.address?.city);
        setValue('lesseeState', newOwner.address?.state?.name);
        setValue('lesseeZipCode', newOwner.address?.zipCode);
        setValue('lesseeOwnerLookupId', newOwner.ownerLookUpId);
        if (!!newOwner.phoneNumberChanges?.phoneNumbers?.find(pn => pn.isPrimary === true)) {
            setValue('lesseePhoneNumber', newOwner.phoneNumberChanges?.phoneNumbers?.find(pn => pn.isPrimary === true).number);
        } else if (newOwner.phoneNumberChanges?.phoneNumbers?.length > 0) {
            setValue('lesseePhoneNumber', newOwner.phoneNumberChanges?.phoneNumbers[0].number);
        } else {
            setValue('lesseePhoneNumber', '');
        }
    };

    const openOwnerDialog = () => {
        dialogContext.owners.manageOwner(false, onOwnerSelected);
    };

    const onSetAmendedValue = (index: number) => {
        let rep = getValues();
        if (rep.contractAmendments !== undefined && rep.contractAmendments[index]?.amendedField !== undefined) {
            let contractDataProp = rep.contractAmendments[index].amendedField;
            setValue(`contractAmendments[${index}].oldValue`, contractData[contractDataProp]);
        }
    };

    const calculateTotal = (
        measurementTypeId: number,
        paymentAmount: number,
        netAcres: number,
        measurementTypesList: Array<IndexReferenceType>
    ) => {
        let measurementType = measurementTypesList.find((mt) => mt.id === measurementTypeId)?.name;

        return measurementType !== undefined ? Number(MathUtil.cleanDollarAmount(measurementType === 'Per Item' ? paymentAmount * netAcres : paymentAmount)) : 0;
    };

    const infoToSecondWindow = () => {
        let secondWindowInfo: IContract = contractData;

        dispatch(actions.layout.pushToSecondWindow('/lease-form'));
        dispatch(actions.navigation.pushForm({ contractData: secondWindowInfo, contractClauses }));
        setSecondWindowOpen(false);
    };

    // const onSecondWindowNavigate = () => {
    //     dispatch(actions.layout.openSecondWindow());
    //     setSecondWindowOpen(true);
    // };

    // FORM DATA REFERENCES
    const checkDisabled = (index: number) => {
        let rep = getValues();
        if (rep.contractAmendments !== undefined) {
            return parseInt(rep.contractAmendments[index]?.id, 10) > 0;
        }
        return false;
    };

    const amendmentFields = () => {
        const fields = Object.getOwnPropertyNames(contractData);
        let filteredFields = fields.filter((f: string) => {
            return f !== 'id' && f !== 'isGeneratedLease';
        });
        return filteredFields.map((field) => {
            return { value: field, label: field };
        });
    };

    // REACT HTML ENTITIES
    const correctOwnerName = () => {
        if (!!ownerData?.ownerDetails) {
            const ownerInfo = OwnerUtil.MapOwner(ownerData?.ownerDetails);

            if (ownerInfo instanceof OwnerIndividual) {
                return `${ownerInfo?.firstName}${ownerInfo?.middleName ? ` ${ownerInfo?.middleName} ` : ' '}${ownerInfo?.lastName}`;
            } else {
                return ownerInfo.ownerName();
            }
        }
        return 'N/A';
    };

    const displayOwnerDetails = () => {
        if (ownerData?.ownerDetails) {
            const ownerDetails = OwnerUtil.MapOwner(ownerData.ownerDetails);

            if (ownerDetails instanceof OwnerJointTenant) {
                return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', margin: 'auto 2rem' }}>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <span style={{ fontSize: 'medium', fontWeight: 'bold' }}>Name:</span>
                            <span style={{ color: '#6393c1' }}>{ownerDetails?.ownerName()}</span>
                        </div>

                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <span style={{ fontSize: 'medium', fontWeight: 'bold' }}>Address:</span>
                            <div>
                                <span style={{ color: '#6393c1' }}>{!!ownerDetails?.addressLine1 ? ownerDetails?.addressLine1 + ', ' : ''}</span>
                                <span style={{ color: '#6393c1' }}>{!!ownerDetails?.addressLine2 ? ownerDetails?.addressLine2 + ', ' : ''}</span>
                                <span style={{ color: '#6393c1' }}>{!!ownerDetails?.city ? ownerDetails?.city + ', ' : ''}</span>
                                <span style={{ color: '#6393c1' }}>{!!ownerDetails?.state ? ownerDetails?.state + ', ' : ''}</span>
                                <span style={{ color: '#6393c1' }}>{ownerDetails?.zipCode}</span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <span style={{ fontSize: 'medium', fontWeight: 'bold' }}>Email:</span>
                            <span style={{ color: '#6393c1' }}>{ownerDetails.email || 'No Email'}</span>
                        </div>
                    </div>
                );
            } else if (ownerDetails instanceof OwnerIndividual) {
                return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', margin: 'auto 2rem' }}>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <span style={{ fontSize: 'medium', fontWeight: 'bold' }}>Name:</span>
                            <span style={{ color: '#6393c1' }}>
                                {`${ownerDetails?.firstName}${!!ownerDetails?.middleName ? ` ${ownerDetails?.middleName} ` : ' '}${ownerDetails?.lastName
                                    }`}
                            </span>
                        </div>

                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <span style={{ fontSize: 'medium', fontWeight: 'bold' }}>Address:</span>
                            <div>
                                <span style={{ color: '#6393c1' }}>
                                    {!!ownerDetails?.address?.addressLine1 ? ownerDetails?.address?.addressLine1 + ', ' : ''}
                                </span>
                                <span style={{ color: '#6393c1' }}>
                                    {!!ownerDetails?.address?.addressLine2 ? ownerDetails?.address?.addressLine2 + ', ' : ''}
                                </span>
                                <span style={{ color: '#6393c1' }}>{!!ownerDetails?.address?.city ? ownerDetails?.address?.city + ', ' : ''}</span>
                                <span style={{ color: '#6393c1' }}>
                                    {!!ownerDetails.address?.state?.name ? ownerDetails.address?.state?.name + ', ' : ''}
                                </span>
                                <span style={{ color: '#6393c1' }}>{ownerDetails.address?.zipCode}</span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <span style={{ fontSize: 'medium', fontWeight: 'bold' }}>Email:</span>
                            <span style={{ color: '#6393c1' }}>{ownerDetails.email || 'No Email'}</span>
                        </div>
                    </div>
                );
            } else {
                return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', margin: 'auto 2rem' }}>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <span style={{ fontSize: 'medium', fontWeight: 'bold' }}>Name:</span>
                            <span style={{ color: '#6393c1' }}>{ownerDetails?.ownerName()}</span>
                        </div>

                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <span style={{ fontSize: 'medium', fontWeight: 'bold' }}>Address:</span>
                            <div>
                                <span style={{ color: '#6393c1' }}>
                                    {!!ownerDetails?.address?.addressLine1 ? ownerDetails?.address?.addressLine1 + ', ' : ''}
                                </span>
                                <span style={{ color: '#6393c1' }}>
                                    {!!ownerDetails?.address?.addressLine2 ? ownerDetails?.address?.addressLine2 + ', ' : ''}
                                </span>
                                <span style={{ color: '#6393c1' }}>{!!ownerDetails?.address?.city ? ownerDetails?.address?.city + ', ' : ''}</span>
                                <span style={{ color: '#6393c1' }}>
                                    {!!ownerDetails.address?.state?.name ? ownerDetails.address?.state?.name + ', ' : ''}
                                </span>
                                <span style={{ color: '#6393c1' }}>{ownerDetails.address?.zipCode}</span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <span style={{ fontSize: 'medium', fontWeight: 'bold' }}>Email:</span>
                            <span style={{ color: '#6393c1' }}>{ownerDetails.email || 'No Email'}</span>
                        </div>
                    </div>
                );
            }
        } else {
            return <div>No Owner Data</div>;
        }
    };

    const onRevokeInHand = () => {
        let formData = getValues();
        formData.inHandDate = null;
        formData.isInHand = false;
        if (formData?.tracts?.length > 0) {
            formData?.tracts?.forEach((t: ContractGenerationTractDetail) => {
                if (t.includeTract === true) {
                    t.leasedStatus = 'Open';
                }
            });
        }
        onSubmit(formData as Lease);
    };

    const refreshTractMORs = () => {
        let files = new Array<ReportFileDetails>();
        let tractList = contractData?.tracts?.length > 0 ? contractData.tracts.filter(tr => tr.includeTract === true) : [];
        for (let x = 0; x < tractList?.length; x++) {
            // tslint:disable-next-line:max-line-length
            let globalId = appContext.clientNumber + `.` + appContext.projectNumber + `.` + tractList[x].sectionNumber + `.` + tractList[x].tractNumber + `-` + tractList[x].horizonNumber;
            let tractType = tractList[x].type.toLowerCase().search('surface') > 0 ? 'Surface Tract' : 'Mineral Tract';
            files.push({ reportFileType: Constants.refreshMORReportFileType, tractType: tractType, globalId: globalId });
        }
        setTractMORList(files);
        console.log('Refreshing MORs...');
    };

    const displayAmendments = () => {
        if (amendments?.length > 0) {
            return amendments.map((amendment: ContractAmendment, index: number) => {
                return (
                    <div
                        key={amendment.id}
                        style={{
                            display: 'flex',
                            border: '2px solid #ccc',
                            borderRadius: '5px',
                            flexWrap: 'wrap',
                            rowGap: '1rem',
                            alignItems: 'center',
                            justifyContent: 'space-around',
                            padding: '1rem 1rem',
                            margin: '1rem',
                        }}
                    >
                        <div className="mr-1" hidden={true}>
                            <RHFInputBox label="" name={`contractAmendments[${index}].id`} register={register} errors={errors} disabled={true} />
                            <RHFCheckbox name="isGenerated" label="" disabled={true} register={register} errors={errors} />
                        </div>
                        <RHFSelect
                            name={`contractAmendments[${index}].amendmentType`}
                            label="Amendment Type"
                            register={register}
                            options={[
                                { value: 'Terms', label: 'Terms' },
                                { value: 'Ratification', label: 'Ratification' },
                                { value: 'Misc', label: 'Misc' },
                            ]}
                            disabled={checkDisabled(index)}
                            bodyStyle={{ width: '23%', marginLeft: '1rem' }}
                            style={{ width: '100%' }}
                        />
                        <RHFSelect
                            name={`contractAmendments[${index}].amendedField`}
                            label="Amendment Field"
                            register={register}
                            options={amendmentFields()}
                            onChange={() => onSetAmendedValue(index)}
                            disabled={checkDisabled(index)}
                            bodyStyle={{ width: '23%' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            name={`contractAmendments[${index}].oldValue`}
                            label="Old Value"
                            register={register}
                            errors={errors}
                            disabled={true}
                            bodyStyle={{ width: '23%' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            name={`contractAmendments[${index}].newValue`}
                            label="New Value"
                            register={register}
                            errors={errors}
                            disabled={checkDisabled(index)}
                            bodyStyle={{ width: '23%', marginRight: '1.2rem' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            name={`contractAmendments[${index}].recordedDate`}
                            label="Recorded Date"
                            type="date"
                            register={register}
                            errors={errors}
                            disabled={checkDisabled(index)}
                            bodyStyle={{ width: '31%', marginLeft: '1rem' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            name={`contractAmendments[${index}].book`}
                            label="Book"
                            register={register}
                            errors={errors}
                            disabled={checkDisabled(index)}
                            bodyStyle={{ width: '31%' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            name={`contractAmendments[${index}].page`}
                            label="Page"
                            register={register}
                            errors={errors}
                            disabled={checkDisabled(index)}
                            bodyStyle={{ width: '31%', marginRight: '1rem' }}
                            style={{ width: '100%' }}
                        />
                    </div>
                );
            });
        } else {
            return <div>No Amendments</div>;
        }
    };

    // REACT BODY
    return (
        <div>
            <div style={{ height: '74.2vh', overflow: 'hidden' }}>
                {isNewContract && (
                    <>
                        <div
                            id="dirtyFlag"
                            style={{
                                position: 'fixed',
                                top: '4.7rem',
                                left: '0',
                                width: '100%',
                                height: '3rem',
                                color: 'white',
                                backgroundColor: 'rgba(255,0,0,.5)',
                                zIndex: 9000,
                                padding: '1rem 0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {`New ${contractTypeName}`}
                        </div>
                    </>
                )}

                <div>
                    <div style={{ textAlign: 'center' }}>
                        <h1 style={{ marginTop: '0', marginBottom: '1rem' }}>{`${contractTypeName} Generation`}</h1>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '3rem', margin: 'auto 5rem' }}>
                        <div style={{ display: 'flex', marginBottom: '1rem', gap: '1rem', flexDirection: 'column' }}>
                            {/* add header with owner info */}
                            <div style={{ marginRight: '1rem' }}>
                                <span style={{ marginRight: '0.5rem', fontWeight: 'bold' }} className="header-info">
                                    Agreement Type:
                                </span>
                                <span className="header-info" style={{ fontWeight: 'bold', color: '#6393c1' }}>
                                    Paid Up Lease
                                </span>
                            </div>
                            <div style={{ marginRight: '1rem' }}>
                                <span style={{ marginRight: '0.5rem', fontWeight: 'bold' }} className="header-info">
                                    Owner:
                                </span>
                                <span className="header-info" style={{ fontWeight: 'bold', color: '#6393c1' }}>
                                    {correctOwnerName()}
                                </span>
                            </div>
                            <div style={{ marginRight: '1rem' }}>
                                <span style={{ marginRight: '0.5rem', fontWeight: 'bold' }} className="header-info">
                                    Doc Number:
                                </span>
                                <span className="header-info" style={{ fontWeight: 'bold', color: '#6393c1' }}>
                                    {leaseNumber}
                                </span>
                            </div>
                            <div style={{ marginRight: '1rem' }}>
                                <span style={{ marginRight: '0.5rem', fontWeight: 'bold' }} className="header-info">
                                    Invoice Status:
                                </span>
                                <span className="header-info" style={{ fontWeight: 'bold', color: '#6393c1' }}>
                                    {invoiceData?.length > 0 ? invoiceData.map((it, index) => {
                                        let status = 'Pending';
                                        if (!!it.void_date) {
                                            status = 'VOID';
                                        } else if (!!it.check_date) {
                                            status = 'PAID';
                                            status += !!it.client_paid_date ? '' : ', NOT FUNDED';
                                        } else if (!!it.client_paid_date) {
                                            status = 'FUNDED';
                                        } else if (!!it.ar_date) {
                                            status = 'POSTED';
                                            console.log('just to updates');
                                        }
                                        return <span key={index}> {it.invoice_no1} - {status} </span>;
                                    }) : 'N/A'}
                                </span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', rowGap: '2rem', alignItems: 'center', bottom: '1rem' }}>
                            <div style={{ marginRight: '1rem' }}>
                                <span style={{ marginRight: '0.5rem', fontWeight: 'bold' }} className="header-info">
                                    Contract Status:
                                </span>
                                <span className="header-info" style={{ fontWeight: 'bold', color: 'rgba(255,0,0,.5)' }}>
                                    {!!contractData?.isCommitted && !!contractData?.isInHand && 'Committed & In-Handed'}
                                    {!!contractData?.isCommitted && !contractData?.isInHand && 'Committed'}
                                    {!contractData?.isCommitted && !!contractData?.isInHand && 'In-Handed'}
                                    {!contractData?.isCommitted && !contractData?.isInHand && !contractData?.isVoid && 'Rough-Draft'}
                                    {!!contractData?.isVoid && !contractData?.isInHand && !contractData?.isCommitted && 'Void'}
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                {contractData?.isCommitted !== true &&
                                    <Button className="blue-hover-button" text="Commit Contract" onClick={showStatusDialog} disabled={isNewContract || formLoading} />}
                                {contractData?.isCommitted === true &&
                                    <Button className="blue-hover-button" text="Uncommit Contract" onClick={showUncommitConfirmationDialog} disabled={isNewContract || formLoading} />}
                                {!contractData?.isInHand && <Button className="blue-hover-button" text="In-Hand Contract" onClick={onInHand} disabled={isNewContract || formLoading} />}
                                {/* tslint:disable-next-line: max-line-length */}
                                {contractData?.isInHand && appContext.currentUser.isAdmin && <Button className="blue-hover-button" text="Revoke In-Hand" onClick={showConfirmRevokeInhandDialog} disabled={isNewContract || formLoading} />}
                                {/* <Button className="blue-hover-button" text="Live Lease" onClick={onSecondWindowNavigate} disabled={isNewContract || formLoading} /> */}
                                {(contractData && contractType === LandContractType.BrineLease) &&
                                    <Button className="blue-hover-button" text="Get Changelog" onClick={getChangeLog} disabled={!ownershipId} />}
                            </div>
                        </div>
                    </div>
                </div>

                {/* <dialog></dialog> */}

                <div style={{ display: 'flex', height: '57vh', gap: '1rem' }}>
                    <div
                        style={{
                            backgroundColor: '#6393c1',
                            width: '20rem',
                            color: 'white',
                            overflowY: 'scroll',
                            scrollbarWidth: 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-evenly'
                        }}
                    >
                        <section style={{ textAlign: 'center' }}>
                            <h3 style={{ fontSize: '1.75rem', margin: '.3rem 0' }}>Contract Sections</h3>
                        </section>
                        <hr style={{ margin: '0', color: '#ccc' }} />
                        <section
                            className="border-hover-effect"
                            onClick={() => scrollToSection(generInfoSection)}
                        >
                            <span>General Information</span>
                        </section>
                        <hr style={{ margin: '0', color: '#ccc' }} />
                        <section
                            className="border-hover-effect"
                            onClick={() => scrollToSection(lesseeInfoSection)}
                        >
                            <span>Lessee Information</span>
                        </section>
                        <hr style={{ margin: '0', color: '#ccc' }} />
                        <section
                            className="border-hover-effect"
                            onClick={() => scrollToSection(ownerInfoSection)}
                        >
                            <span>Owner Information</span>
                        </section>
                        <hr style={{ margin: '0', color: '#ccc' }} />
                        <section
                            className="border-hover-effect"
                            onClick={() => scrollToSection(tractListSection)}
                        >
                            <span>Tract List Section</span>
                        </section>
                        <hr style={{ margin: '0', color: '#ccc' }} />
                        {(<section
                            className="border-hover-effect"
                            onClick={() => scrollToSection(agreementTermSection)}
                        >
                            <span>Contract Terms</span>
                        </section>)}
                        {/* {(<hr style={{ margin: '0', color: '#ccc' }} />)}
                        {contractType === LandContractType.BrineLease && (<section
                            className="border-hover-effect"
                            onClick={() => scrollToSection(provisionSection)}
                        >
                            <span>Provisions</span>
                        </section>)} */}
                        {contractType === LandContractType.BrineLease && (<hr style={{ margin: '0', color: '#ccc' }} />)}
                        <section
                            className="border-hover-effect"
                            onClick={() => scrollToSection(addendumSection)}
                        >
                            <span>Addendum Section</span>
                        </section>
                        <hr style={{ margin: '0', color: '#ccc' }} />
                        <section
                            className="border-hover-effect"
                            onClick={() => scrollToSection(adHocSection)}
                        >
                            <span>Ad Hoc Section</span>
                        </section>
                        <hr style={{ margin: '0', color: '#ccc' }} />
                        <section
                            className="border-hover-effect"
                            onClick={() => scrollToSection(acknowledgementSec)}
                        >
                            <span>Acknowledgement Section</span>
                        </section>
                        <hr style={{ margin: '0', color: '#ccc' }} />
                        <section
                            className="border-hover-effect"
                            onClick={() => scrollToSection(docManagSection)}
                        >
                            <span>Document Management</span>
                        </section>
                        <hr style={{ margin: '0', color: '#ccc' }} />
                        <section
                            className="border-hover-effect"
                            onClick={() => scrollToSection(pmSection)}
                        >
                            <span>Maintenance Section</span>
                        </section>
                        <hr style={{ margin: '0', color: '#ccc' }} />
                        <section style={{ textAlign: 'center', marginTop: '1rem', marginBottom: '2rem' }}>
                            <button style={{ padding: '1rem' }} form="contractForm" disabled={(!tractsSelected || isInHand || formLoading) && ownerData?.tracts?.length !== 0}>
                                Save & Submit
                            </button>
                            {/* <Button text="Save & Submit" intent="primary" formId="contractForm" /> */}
                        </section>
                        <hr style={{ margin: '0', color: '#ccc' }} />
                        <section style={{ background: 'black' }} />
                    </div>
                    <div id="formContent" style={{ maxHeight: '70vh', overflowY: 'scroll', width: '85%' }}>
                        <div>
                            <form onSubmit={handleSubmit(onSubmit)} id="contractForm">
                                <div ref={generInfoSection}>
                                    {(isDirty || deltasAdded || formLoading) && !isNewContract && (
                                        <>
                                            <div
                                                id="dirtyFlag"
                                                style={{
                                                    position: 'fixed',
                                                    top: '4.7rem',
                                                    left: '0',
                                                    width: '100%',
                                                    height: '3rem',
                                                    color: 'white',
                                                    backgroundColor: 'rgba(255,0,0,.5)',
                                                    zIndex: 9000,
                                                    padding: '1rem 0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {formLoading && (<div>Loading Data...</div>)}
                                                {!formLoading && (
                                                    <>
                                                        <div>Unsaved Changes - Please Save Before Leaving Form</div>
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                right: '.5rem',
                                                                top: '50%',
                                                                transform: 'translateY(-50%)',
                                                                display: 'flex',
                                                            }}
                                                        >
                                                            <div>
                                                                <Button
                                                                    text="Save"
                                                                    type="submit"
                                                                    size="small"
                                                                    intent="primary"
                                                                    disabled={!tractsSelected || formLoading}
                                                                    style={{ height: '2.5rem' }}
                                                                />
                                                            </div>
                                                            <div style={{ margin: '0 1rem', borderRight: '1px solid white', width: '1px' }} />
                                                            <Button
                                                                text="Cancel Changes"
                                                                size="small"
                                                                onClick={() => reset(initialValues())}
                                                                style={{ height: '2.5rem' }}
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div ref={generInfoSection} style={{ marginBottom: '3rem' }}>
                                    <RHFLegend title="General Information" />
                                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' }}>
                                        <RHFInputBox
                                            type="text"
                                            name="clientDocNumber"
                                            label="Client Doc Number"
                                            register={register}
                                            errors={errors}
                                            disabled={isInHand}
                                            bodyStyle={{ width: '31%', marginLeft: '1rem' }}
                                            style={{ width: '100%' }}
                                        />
                                        <RHFInputBox
                                            type="text"
                                            name="leasedBy"
                                            label="Leased By"
                                            register={register}
                                            errors={errors}
                                            isRequired={true}
                                            disabled={isInHand}
                                            bodyStyle={{ width: '31%' }}
                                            style={{ width: '100%' }}
                                        />
                                        <RHFInputBox
                                            type="text"
                                            name="reminderNote"
                                            label="Reminder Note"
                                            register={register}
                                            errors={errors}
                                            disabled={isInHand}
                                            bodyStyle={{ width: '31%', marginRight: '1rem' }}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', marginTop: '1rem' }}>
                                        <RHFInputBox
                                            type="date"
                                            name="reminderDate"
                                            label="Reminder Date"
                                            register={register}
                                            errors={errors}
                                            disabled={isInHand}
                                            bodyStyle={{ width: '31%', marginLeft: '1rem' }}
                                            style={{ width: '100%' }}
                                        />
                                        {/* <RHFInputBox type="hidden" name="leaseNumber" label="" register={register} errors={errors} /> */}
                                        <input type="hidden" name="leaseNumber" ref={register()} />
                                        <input type="hidden" name="lesseeOwnerLookupId" ref={register()} />
                                        <RHFInputBox
                                            type="date"
                                            name="commitDate"
                                            label="Committed Date"
                                            register={register}
                                            errors={errors}
                                            disabled={true}
                                            bodyStyle={{ width: '31%' }}
                                            style={{ width: '100%' }}
                                        />
                                        <RHFInputBox
                                            type="date"
                                            name="inHandDate"
                                            label="In-Hand Date"
                                            register={register}
                                            errors={errors}
                                            disabled={true}
                                            bodyStyle={{ width: '31%', marginRight: '1rem' }}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>

                                <div ref={lesseeInfoSection} style={{ marginBottom: '3rem' }}>
                                    <RHFLegend title="Lessee Information" />

                                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', rowGap: '1rem' }}>
                                        <RHFInputBox
                                            rules={{ required: 'field required' }}
                                            name="lesseeName"
                                            label="Lessee"
                                            register={register}
                                            errors={errors}
                                            isRequired={true}
                                            disabled={isInHand}
                                            bodyStyle={{ width: '31%', marginLeft: '1rem' }}
                                            style={{ width: '100%' }}
                                        />
                                        <RHFInputBox
                                            name="originalLessee"
                                            label="Original Grantee"
                                            register={register}
                                            errors={errors}
                                            disabled={isInHand}
                                            bodyStyle={{ width: '31%' }}
                                            style={{ width: '100%' }}
                                        />
                                        <RHFCheckbox
                                            name="isNotOriginalLessee"
                                            label="Not Original Lessee"
                                            register={register}
                                            errors={errors}
                                            disabled={isInHand}
                                            bodyStyle={{ width: '31%', marginTop: '1.5rem', marginRight: '1rem' }}
                                        />
                                        <RHFInputBox
                                            rules={{ required: 'field required' }}
                                            name="lesseeAddressLine1"
                                            label="Address Line 1"
                                            register={register}
                                            errors={errors}
                                            isRequired={true}
                                            disabled={isInHand}
                                            bodyStyle={{ width: '47%', marginLeft: '1rem' }}
                                            style={{ width: '100%' }}
                                        />
                                        <RHFInputBox
                                            name="lesseeAddressLine2"
                                            label="Address Line 2"
                                            register={register}
                                            errors={errors}
                                            disabled={isInHand}
                                            bodyStyle={{ width: '47%', marginRight: '1rem' }}
                                            style={{ width: '100%' }}
                                        />
                                        <RHFInputBox
                                            name="lesseeCity"
                                            label="City"
                                            register={register}
                                            errors={errors}
                                            rules={{ required: 'field required' }}
                                            isRequired={true}
                                            disabled={isInHand}
                                            bodyStyle={{ width: '22%', marginLeft: '1rem' }}
                                            style={{ width: '100%' }}
                                        />
                                        <RHFSelect
                                            label="State"
                                            name="lesseeState"
                                            options={
                                                !!states
                                                    ? states?.items?.map((s) => {
                                                        return { value: s.name, label: `${s.name}` };
                                                    })
                                                    : []
                                            }
                                            register={register}
                                            errors={errors}
                                            rules={{ required: 'field required' }}
                                            defaultVal={!!contractData ? `${contractData.lesseeState}` : ''}
                                            isRequired={true}
                                            disabled={isInHand}
                                            bodyStyle={{ width: '22%' }}
                                            style={{ width: '100%' }}
                                        />
                                        <RHFInputBox
                                            name="lesseeZipCode"
                                            label="Zip Code"
                                            register={register}
                                            errors={errors}
                                            isRequired={true}
                                            disabled={isInHand}
                                            bodyStyle={{ width: '22%' }}
                                            style={{ width: '100%' }}
                                        />
                                        <RHFInputBox
                                            name="lesseePhoneNumber"
                                            label="Phone Number"
                                            register={register}
                                            errors={errors}
                                            disabled={isInHand}
                                            bodyStyle={{ width: '22%', marginRight: '1rem' }}
                                            style={{ width: '100%' }}
                                        />
                                        {/* conditional render based on not original lessee checkbox */}

                                    </div>

                                    <div className="mt-3 ml-2">
                                        <Button
                                            className="blue-hover-button"
                                            text="USE DEFAULT LESSEE"
                                            onClick={useDefaultLessee}
                                            disabled={isInHand}
                                            style={{ marginLeft: '1rem' }}
                                        />
                                        <Button
                                            className="blue-hover-button"
                                            text="USE DIFFERENT OWNER"
                                            onClick={openOwnerDialog}
                                            disabled={isInHand}
                                            style={{ marginLeft: '.5rem' }}
                                        />
                                        {/* <Button text="CREATE LESSEE" /> */}
                                    </div>
                                </div>

                                {/* make multi owner */}
                                <div ref={ownerInfoSection} style={{ marginBottom: '2rem' }}>
                                    <RHFLegend title="Owner Information" />

                                    <div style={{ marginBottom: '1rem' }}>
                                        {/* owner info label here */}
                                        {displayOwnerDetails()}
                                    </div>

                                    <div style={{ marginBottom: '1rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0 2rem 1rem 2rem' }}>
                                            <RHFInputBox
                                                name="ownersOnDocument"
                                                label="Owners on Document"
                                                register={register}
                                                errors={errors}
                                                disabled={isInHand}
                                                bodyStyle={{ width: '50%' }}
                                                style={{ width: '100%' }}
                                            />
                                            <RHFInputBox
                                                name="ownersSSNTaxIdOnDocument"
                                                label="Owner SSN/Tax Id on Document"
                                                register={register}
                                                errors={errors}
                                                disabled={isInHand}
                                                bodyStyle={{ width: '50%' }}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0 2rem 1rem 2rem' }}>
                                            <RHFInputBox
                                                name="ownersAddressOnDocument"
                                                label="Owner Address on Document"
                                                register={register}
                                                errors={errors}
                                                disabled={isInHand}
                                                bodyStyle={{ width: '100%' }}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                        <div style={{ margin: '0 2rem 1rem 2rem' }}>
                                            {/* <RHFInputBox name="clientFacingNotes" label="Buyer Lease Notes" register={register} errors={errors} /> */}
                                            <label>Buyer Lease Notes</label>
                                            <textarea
                                                name="leaseLessorNotes"
                                                ref={register()}
                                                style={{ width: '100%', height: '10rem' }}
                                                disabled={isInHand}
                                            />
                                        </div>

                                        <div style={{ margin: 'auto 2rem' }}>
                                            <Button
                                                className="blue-hover-button"
                                                text="Use Owner Info"
                                                onClick={updateOwnerData}
                                                disabled={isInHand}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div hidden={true}>
                                    <RHFInputBox label="" name="id" register={register} type="number" disabled={true} errors={errors} />
                                </div>

                                <div ref={tractListSection} style={{ marginBottom: '2rem' }}>
                                    <div style={{ margin: 'auto 2rem' }}>
                                        <RHFLegend title="Tract List" />
                                        {ownerData?.tracts?.length > 0 ? (
                                            <LeasingTractList
                                                ownerData={ownerData}
                                                totalGross={totalGross}
                                                totalNet={totalNet}
                                                register={register}
                                                errors={errors}
                                                includeTract={updateLegalDescription}
                                                tracts={leaseTractList}
                                                originalTractList={originalTractList}
                                                inHand={isInHand}
                                                parentTractType={project?.currentProject?.tractTypeName}
                                                isEasternStates={project?.currentProject?.landSystemId === LandSystem.Eastern}
                                                clientNumber={appContext.clientNumber}
                                                projectNumber={appContext.projectNumber}
                                                tractLimit={tractContextType}
                                            />
                                        ) : (
                                            <div>No Tracts</div>
                                        )}

                                        <div>
                                            <span>
                                                Legal Description ({' '}
                                                {!moveLegalDescription ? (
                                                    '0'
                                                ) : moveLegalDescription?.length > 1000 ? (
                                                    <abbr style={{ color: 'red' }} title="Legal Description moved to Exhibit A">
                                                        {moveLegalDescription?.length}
                                                    </abbr>
                                                ) : (
                                                    moveLegalDescription?.length
                                                )}{' '}
                                                / 1000 )
                                            </span>
                                            {/* {contractType !== LandContractType.BrineLease && (
                                                <RHFTextArea
                                                    label=""
                                                    name="legalDescription"
                                                    register={register}
                                                    style={{ width: '100%', height: '10rem' }}
                                                    rules={{ required: 'field required', minLength: 1 }}
                                                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setMoveLegalDescription(e.target.value)}
                                                    disabled={isInHand}
                                                />
                                            )} */}
                                            <Controller
                                                name="rteLegalDescription"
                                                control={control}
                                                defaultValue=""
                                                render={({ onChange, onBlur, value }) => (
                                                    <ReactQuill theme="snow" value={value} onChange={onChange} onBlur={onBlur} />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div ref={agreementTermSection} />
                                <div ref={provisionSection} />

                                {contractType === LandContractType.BatteryStorage && (
                                    <div>
                                        <BatteryStorageGeneration
                                            batteryStorageData={contractData as BatteryStorage}
                                            measurementTypes={measurementTypes}
                                            isInHanded={isInHand}
                                            totalNet={totalNet}
                                            getValues={getValues}
                                            register={register}
                                            errors={errors}
                                            setValue={setValue}
                                            calculateTotal={calculateTotal}
                                        />
                                    </div>
                                )}
                                {contractType === LandContractType.SurfaceWaiver && (
                                    <div>
                                        <SurfaceWaiverGeneration
                                            surfaceWaiverData={contractData as SurfaceWaiver}
                                            measurementTypes={measurementTypes}
                                            isInHanded={isInHand}
                                            getValues={getValues}
                                            register={register}
                                            errors={errors}
                                            setValue={setValue}
                                            calculateTotals={calculateTotalBonusPaid}
                                        />
                                    </div>
                                )}
                                {contractType === LandContractType.SolarLease && (
                                    <div>
                                        <SolarLeaseGeneration
                                            solarLeaseData={contractData as SolarLease}
                                            measurementTypes={measurementTypes}
                                            isInHanded={isInHand}
                                            totalGross={totalGross}
                                            getValues={getValues}
                                            setValue={setValue}
                                            register={register}
                                            errors={errors}
                                        />
                                    </div>
                                )}
                                {contractType === LandContractType.BrineLease && (
                                    <div>
                                        <BrineLeaseGeneration
                                            brineLeaseData={contractData as BrineLease}
                                            measurementTypes={measurementTypes}
                                            provisionalClauses={provisionLeaseClauses}
                                            isInHanded={isInHand}
                                            totalNet={totalNet}
                                            getValues={getValues}
                                            setValue={setValue}
                                            register={register}
                                            errors={errors}
                                            calculateTotal={calculateTotal}
                                        />
                                    </div>
                                )}

                                <div style={{ marginBottom: '2rem' }} ref={addendumSection}>
                                    <RHFLegend title="Client Approved Clauses" />
                                    {/* add Addendum Clauses here */}
                                    {!!contractData && contractClauses?.length > 0 ? (
                                        <div>
                                            <ClientApprovedLeaseClauses
                                                contractClauses={contractClauses}
                                                selectedClauses={selectedClauses}
                                                isInHanded={isInHand}
                                                updateProcessing={updateProcessing}
                                                getValues={getValues}
                                                register={register}
                                                errors={errors}
                                            />
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: 'center' }}>
                                            <p>No Addendum Clauses!</p>
                                        </div>
                                    )}
                                </div>

                                {leaseClauseTypes?.length > 0 && (
                                    <div ref={adHocSection} style={{ marginBottom: '4rem' }}>
                                        <RHFLegend title="Ad Hoc Clauses" />
                                        {/* add Ad Hoc Clauses here */}
                                        <AdHocClauses
                                            register={register}
                                            errors={errors}
                                            disabled={isNewContract || isInHand}
                                            leaseClauseTypes={leaseClauseTypes}
                                            getValues={getValues}
                                            setValue={setValue}
                                            namePrefix="adHocClauses"
                                            leaseClauses={adHocClauses}
                                        />
                                    </div>
                                )}

                                <div ref={acknowledgementSec}>
                                    <RHFLegend title="Acknowledgment & Signature" />
                                    <div style={{ margin: '2rem' }}>
                                        <AcknowledgementList
                                            acknowledgements={!!contractData?.acknowledgements ? contractData.acknowledgements : []}
                                            acknowledgementType={!!acknowledgementTypes ? acknowledgementTypes : []}
                                            states={!!states ? states?.items : []}
                                            olderVersions={hasOlderTemplates}
                                            invoiceableLookupId={invoiceableLookupId}
                                            refreshLease={refreshPage}
                                            isNewLease={isNewContract || isInHand}
                                            hideDialog={hideDialog}
                                        />
                                    </div>
                                </div>

                                <div ref={docManagSection} style={{ marginBottom: '2rem' }}>
                                    <RHFLegend title="Manage Documents" />
                                    {/* add Document Management here */}
                                    <AgreementDocs
                                        generateLPR={showLPRPDFDialog}
                                        generateLease={showLeasePDFDialog}
                                        generateMemo={showMemoPDFDialog}
                                        generateOop={showOopPDFDialog}
                                        updateOlderVersions={(hasOlderVersion) => setHasOlderTemplates(hasOlderVersion)}
                                        isNewLease={false}
                                        contextType={contractType}
                                        contractLookupId={contractLookupId}
                                        leaseNumber={leaseNumber}
                                        refreshMORs={refreshTractMORs}
                                        filesToUpload={tractMORList}
                                    />
                                </div>

                                <div ref={pmSection} style={{ marginBottom: '4rem' }}>
                                    <RHFLegend title="Processing and Maintenance" />
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            justifyContent: 'space-around',
                                            rowGap: '1rem',
                                            marginBottom: '2rem',
                                        }}
                                    >
                                        <RHFInputBox label="Volume" name="volume" register={register} type="text" errors={errors} bodyStyle={{ width: '150px' }} />
                                        <RHFInputBox label="Page" name="page" register={register} type="text" errors={errors} bodyStyle={{ width: '150px' }} />
                                        <RHFInputBox label="Inst. Number" name="instrumentNumber" register={register} type="text" errors={errors} bodyStyle={{ width: '150px' }} />
                                        <RHFInputBox
                                            label="Recorded Date"
                                            name="recordedDate"
                                            register={register}
                                            type="date"
                                            errors={errors}
                                            bodyStyle={{ width: '150px' }}
                                        />
                                    </div>
                                    {contractType === LandContractType.BrineLease && (
                                        <div className="mb-1" style={{ margin: 'auto 2rem' }}>
                                            <RHFLegend title="Provisions" />

                                            <div style={{ margin: 'auto 2rem' }}>
                                                {provisionLeaseClauses && (
                                                    displayClauses()
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {/* <div style={{ margin: '2rem 2rem 0 2rem' }}>
                                        <fieldset style={{ padding: '.3rem 0', borderWidth: '1px 0 0 0' }}>
                                            <legend style={{ marginLeft: '2rem', padding: '0 .5rem' }}>Option Management</legend>
                                        </fieldset>
                                        <div style={{ display: 'none' }}>
                                            <RHFToggle
                                                name="surfaceWaiverHolds[0].optionExercised"
                                                label="Exercise Option"
                                                register={register}
                                                errors={errors}
                                                disabled={isInHand}
                                            />
                                        </div>
                                        <Button text="Exercise Option" onClick={exerciseOption} disabled={isNewContract || isInHand} />
                                    </div> */}
                                    <div ref={pmAdmendmentSec} style={{ margin: '2rem 2rem 0 2rem' }}>
                                        <fieldset style={{ padding: '.3rem 0', borderWidth: '1px 0 0 0' }}>
                                            <legend style={{ marginLeft: '2rem', padding: '0 .5rem' }}>Amendments</legend>
                                        </fieldset>
                                        <div>
                                            <Button
                                                className="blue-hover-button"
                                                text="Add Amendment"
                                                onClick={addAmendment}
                                                disabled={isNewContract}
                                                style={{ marginTop: '1rem' }}
                                            />
                                        </div>
                                        <div>{displayAmendments()}</div>
                                    </div>
                                </div>

                                {/* <div>
                        <Button text="Save" type="submit" intent="primary" />
                    </div> */}
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <ContractPdfGeneration
                contractData={contractData}
                contractClauses={contractClauses}
                leaseClauseTypes={leaseClauseTypes}
                measurementTypes={measurementTypes}
                location={location}
                renderTypeId={pdfRenderTypeId}
                netAcres={totalNet}
                hideDialog={hideDialog}
                getValues={getValues}
            />
        </div>
    );
};

export default ContractGenerationPage;


// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/redesign/ContractGenerationComponents/ContractGenerationPage.tsx